import React, { useState, useEffect, useMemo } from 'react';
import { useAuction } from '../../context/AuctionContext';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, writeBatch, doc, orderBy, onSnapshot, setDoc, serverTimestamp, addDoc, where, increment, getDoc } from 'firebase/firestore';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import BlindPoolRound from '../../components/BlindPoolRound';
import TransferRound from '../../components/TransferRound';
import ParticipantTeamModal from '../../components/ParticipantTeamModal';
import { containers, table } from '../../theme/commonStyles';
import { isLeagueStage, getEffectiveStorageId, getMatchDisplayName, getTranslatedStageName } from '../../utils/stageHelpers';
import { REFUNDED_TRANSFER_TYPES } from '../../constants/game';

const RankingTable = ({ ranking, onRowClick, showChampion, showMedals, medalCounts }) => (
    <Box sx={containers.glassContainer}>
        <TableContainer component={Paper}>
            <Table aria-label="ranking table">
                <TableHead>
                    <TableRow>
                        <TableCell sx={table.narrowColumn}>#</TableCell>
                        <TableCell>Deelnemer</TableCell>
                        <TableCell align="right">Wedstrijdpunten</TableCell>
                        <TableCell align="right">Scorepunten</TableCell>
                        <TableCell align="right">W</TableCell>
                        <TableCell align="right">G</TableCell>
                        <TableCell align="right">V</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ranking.map((row, index) => {
                        const isChampion = showChampion && index === 0;
                        const medals = showMedals ? (medalCounts?.[row.uid] || 0) : 0;
                        return (
                        <TableRow key={row.uid || index} hover onClick={() => onRowClick(row)} sx={table.clickableCell}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                {index + 1}
                            </TableCell>
                            <TableCell sx={isChampion ? { color: '#fecb00', fontWeight: 'bold' } : undefined}>
                                {row.name}{isChampion ? ' 🏆' : ''}{medals > 0 ? ' ' + '🥇'.repeat(medals) : ''}
                            </TableCell>
                            <TableCell align="right" sx={table.primaryBoldCell}>
                                {row.matchPoints || 0}
                            </TableCell>
                            <TableCell align="right">
                                {(row.playerPoints || 0) + (row.tacticPoints || 0) + (row.tacticCounter || 0)}
                            </TableCell>
                            <TableCell align="right">{row.wins || 0}</TableCell>
                            <TableCell align="right">{row.draws || 0}</TableCell>
                            <TableCell align="right">{row.losses || 0}</TableCell>
                        </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function AdminGame() {
    const { auctionId } = useAuction();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [matchSelections, setMatchSelections] = useState({});
    const [auctionPlayers, setAuctionPlayers] = useState([]);
    const [schedule, setSchedule] = useState(null);
    const [blindPoolRounds, setBlindPoolRounds] = useState([]);
    const [auction, setAuction] = useState(null);
    const [sportmonksRounds, setSportmonksRounds] = useState([]);
    const [rankings, setRankings] = useState([]);
    const [selectedRound, setSelectedRound] = useState('total');
    const [pouleWinCounts, setPouleWinCounts] = useState({});
    const [matchScores, setMatchScores] = useState({}); // State for calculated match scores
    const [showTacticsAndCaptains, setShowTacticsAndCaptains] = useState(() => {
        const saved = localStorage.getItem(`adminShowTacticsAndCaptains_${auctionId}`);
        return saved ? JSON.parse(saved) : false;
    }); // State for showing tactics and captains

    const [openAssignmentModal, setOpenAssignmentModal] = useState(false);
    const [proposedAssignments, setProposedAssignments] = useState([]);
    const [currentRoundForAssignment, setCurrentRoundForAssignment] = useState(null);
    const [openRecalculateModal, setOpenRecalculateModal] = useState(false);
    const [recalculatePlan, setRecalculatePlan] = useState(null);
    const [roundToRecalculate, setRoundToRecalculate] = useState(null);
    const [confirmDeleteRoundOpen, setConfirmDeleteRoundOpen] = useState(false);
    const [roundToDelete, setRoundToDelete] = useState(null);
    const [confirmLotingOpen, setConfirmLotingOpen] = useState(false);
    const [confirmDeleteLotingOpen, setConfirmDeleteLotingOpen] = useState(false);
    const [confirmDeleteBidOpen, setConfirmDeleteBidOpen] = useState(false);
    const [bidToDelete, setBidToDelete] = useState(null);
    const [finalRankingPoule, setFinalRankingPoule] = useState(null);
    const [confirmClosePouleOpen, setConfirmClosePouleOpen] = useState(false);
    const [confirmReopenPouleOpen, setConfirmReopenPouleOpen] = useState(false);
    const [liveRankingIntermediateRound, setLiveRankingIntermediateRound] = useState(null);
    const [liveRankingFinaleronde, setLiveRankingFinaleronde] = useState(null);
    const [finalRoundParticipants, setFinalRoundParticipants] = useState(null);
    const [confirmStartIntermediateOpen, setConfirmStartIntermediateOpen] = useState(false);
    const [confirmResetIntermediateOpen, setConfirmResetIntermediateOpen] = useState(false);
    const [confirmStartFinalRoundOpen, setConfirmStartFinalRoundOpen] = useState(false);
    const [confirmResetFinalRoundOpen, setConfirmResetFinalRoundOpen] = useState(false);

    // Draft state
    const [draftConfig, setDraftConfig] = useState(null);
    const [confirmDraftLotingOpen, setConfirmDraftLotingOpen] = useState(false);
    const [confirmDraftStartOpen, setConfirmDraftStartOpen] = useState(false);
    const [confirmDraftResetLotingOpen, setConfirmDraftResetLotingOpen] = useState(false);
    const [confirmDraftCloseOpen, setConfirmDraftCloseOpen] = useState(false);
    const [confirmDraftRestartOpen, setConfirmDraftRestartOpen] = useState(false);

    // Confirmation dialog states
    const [confirmAction, setConfirmAction] = useState({ open: false, type: null, roundId: null, roundData: null, title: '', content: '' });

    // Knock-out state
    const [confirmKnockoutLotingOpen, setConfirmKnockoutLotingOpen] = useState(false);
    const [confirmResetKnockoutLotingOpen, setConfirmResetKnockoutLotingOpen] = useState(false);

    // Transferrondes state
    const [transferRounds, setTransferRounds] = useState([]);
    const [trStages, setTrStages] = useState([]);
    const [trRounds, setTrRounds] = useState([]);
    const [trSelectedStage, setTrSelectedStage] = useState('');
    const [trSelectedRound, setTrSelectedRound] = useState('');
    const [trSelectedLeg, setTrSelectedLeg] = useState('');
    const [trLoadingStages, setTrLoadingStages] = useState(false);
    const [trLoadingRounds, setTrLoadingRounds] = useState(false);
    const [confirmStartTransferRoundOpen, setConfirmStartTransferRoundOpen] = useState(false);
    const [confirmCloseTransferRoundOpen, setConfirmCloseTransferRoundOpen] = useState(false);
    const [confirmDeleteTransferRoundOpen, setConfirmDeleteTransferRoundOpen] = useState(false);
    const [transferRoundToAction, setTransferRoundToAction] = useState(null);

    // State for ParticipantTeamModal
    const [selectedParticipantForModal, setSelectedParticipantForModal] = useState(null);
    const [isParticipantModalOpen, setIsParticipantModalOpen] = useState(false);
    const [modalRoundId, setModalRoundId] = useState(null);
    const [modalRoundStatus, setModalRoundStatus] = useState(null);
    const [modalMatchId, setModalMatchId] = useState(null);

    useEffect(() => {
        if (!auctionId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const unsubAuction = onSnapshot(doc(db, 'auctions', auctionId), (docSnap) => {
            if (docSnap.exists()) {
                setAuction(docSnap.data());
            }
        }, (err) => {
            console.error("Error fetching auction data:", err);
            setError("Kon veilingdata niet ophalen.");
        });

        const participantsQuery = query(collection(db, 'auctions', auctionId, 'participants'));
        const transfersQuery = query(collection(db, 'auctions', auctionId, 'auctionTransfers'));
        const scheduleQuery = query(collection(db, 'auctions', auctionId, 'schedule'), orderBy('__name__'));
        const selectionsQuery = query(collection(db, 'auctions', auctionId, 'matchSelections'));
        const auctionPlayersQuery = query(collection(db, 'auctions', auctionId, 'auctionPlayers'));
        const blindPoolRoundsQuery = query(collection(db, 'auctions', auctionId, 'blindPoolRounds'), orderBy('roundNumber', 'desc'));

        const unsubParticipants = onSnapshot(participantsQuery, (snapshot) => {
            setParticipants(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => {
            console.error("Error fetching participants:", err);
            setError("Kon deelnemers niet ophalen.");
        });

        const unsubTransfers = onSnapshot(transfersQuery, (snapshot) => {
            setTransfers(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
        }, (err) => {
            console.error("Error fetching transfers:", err);
            setError("Kon transfers niet ophalen.");
        });

        const unsubSchedule = onSnapshot(scheduleQuery, (snapshot) => {
            if (snapshot.empty) {
                setSchedule(null);
            } else {
                const fetchedSchedule = {};
                snapshot.docs.forEach(doc => {
                    fetchedSchedule[doc.id] = doc.data(); // Store full doc data
                });
                setSchedule(fetchedSchedule);
            }
            setLoading(false);
        }, (err) => {
            console.error("Error fetching schedule:", err);
            setError("Kon speelschema niet ophalen.");
            setLoading(false);
        });

        const unsubSelections = onSnapshot(selectionsQuery, (snapshot) => {
            const selectionsData = {};
            snapshot.docs.forEach(doc => {
                selectionsData[doc.id] = doc.data().selections;
            });
            setMatchSelections(selectionsData);
        }, (err) => {
            console.error("Error fetching selections:", err);
            setError("Kon keuzes van deelnemers niet ophalen.");
        });

        const unsubAuctionPlayers = onSnapshot(auctionPlayersQuery, (snapshot) => {
            setAuctionPlayers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => {
            console.error("Error fetching auction players:", err);
            setError("Kon spelersdata niet ophalen.");
        });

        const unsubBlindPoolRounds = onSnapshot(blindPoolRoundsQuery, (snapshot) => {
            setBlindPoolRounds(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => {
            console.error("Error fetching blind pool rounds:", err);
            setError("Kon blinde pool rondes niet ophalen.");
        });

        // Listener voor transferRounds
        const transferRoundsQuery = query(collection(db, 'auctions', auctionId, 'transferRounds'), orderBy('roundNumber', 'desc'));
        const unsubTransferRounds = onSnapshot(transferRoundsQuery, (snapshot) => {
            setTransferRounds(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => {
            console.error("Error fetching transfer rounds:", err);
            setError("Kon transferrondes niet ophalen.");
        });

        // Listener voor finalRankingPoule
        const finalRankingPouleRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRankingPoule');
        const unsubFinalRankingPoule = onSnapshot(finalRankingPouleRef, (docSnap) => {
            if (docSnap.exists()) {
                setFinalRankingPoule(docSnap.data());
            } else {
                setFinalRankingPoule(null);
            }
        }, (err) => {
            console.error("Error fetching finalRankingPoule:", err);
        });

        // Listener voor liveRankingIntermediateRound
        const liveRankingIntermediateRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingIntermediateRound');
        const unsubLiveRankingIntermediate = onSnapshot(liveRankingIntermediateRef, (docSnap) => {
            if (docSnap.exists()) {
                setLiveRankingIntermediateRound(docSnap.data());
            } else {
                setLiveRankingIntermediateRound(null);
            }
        }, (err) => {
            console.error("Error fetching liveRankingIntermediateRound:", err);
        });

        const liveRankingFinalRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingFinaleronde');
        const unsubLiveRankingFinal = onSnapshot(liveRankingFinalRef, (docSnap) => {
            if (docSnap.exists()) {
                setLiveRankingFinaleronde(docSnap.data());
            } else {
                setLiveRankingFinaleronde(null);
            }
        }, (err) => {
            console.error("Error fetching liveRankingFinaleronde:", err);
        });

        const finalRoundParticipantsRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRoundParticipants');
        const unsubFinalRoundParticipants = onSnapshot(finalRoundParticipantsRef, (docSnap) => {
            if (docSnap.exists()) {
                setFinalRoundParticipants(docSnap.data());
            } else {
                setFinalRoundParticipants(null);
            }
        }, (err) => {
            console.error("Error fetching finalRoundParticipants:", err);
        });

        // Listener voor draft config
        const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
        const unsubDraftConfig = onSnapshot(draftConfigRef, (docSnap) => {
            if (docSnap.exists()) {
                setDraftConfig(docSnap.data());
            } else {
                setDraftConfig(null);
            }
        }, (err) => {
            console.error("Error fetching draft config:", err);
        });

        return () => {
            unsubAuction();
            unsubParticipants();
            unsubTransfers();
            unsubSchedule();
            unsubSelections();
            unsubAuctionPlayers();
            unsubBlindPoolRounds();
            unsubTransferRounds();
            unsubFinalRankingPoule();
            unsubLiveRankingIntermediate();
            unsubLiveRankingFinal();
            unsubFinalRoundParticipants();
            unsubDraftConfig();
        };
    }, [auctionId]);

    // Haal toernooifases op wanneer auction.season beschikbaar is (voor transferrondes tab)
    useEffect(() => {
        const fetchTrStages = async () => {
            if (!auction?.season) return;
            setTrLoadingStages(true);
            try {
                const getStagesForSeason = httpsCallable(functions, 'getStagesForSeason');
                const result = await getStagesForSeason({ seasonId: auction.season });
                setTrStages(result.data || []);
            } catch (err) {
                console.error('Fout bij het ophalen van toernooifases:', err);
            } finally {
                setTrLoadingStages(false);
            }
        };
        fetchTrStages();
    }, [auction?.season]);

    // Haal speelrondes op als League Stage is geselecteerd (voor transferrondes tab)
    useEffect(() => {
        const fetchTrRounds = async () => {
            if (!auction?.season || !trSelectedStage) return;
            if (isLeagueStage(trStages, trSelectedStage)) {
                setTrLoadingRounds(true);
                setTrRounds([]);
                setTrSelectedRound('');
                try {
                    const getRoundsForSeason = httpsCallable(functions, 'getRoundsForSeason');
                    const result = await getRoundsForSeason({ seasonId: auction.season });
                    const roundsData = (result.data || []).sort((a, b) => (b.starting_at || '').localeCompare(a.starting_at || ''));
                    setTrRounds(roundsData);
                } catch (err) {
                    console.error('Fout bij het ophalen van speelrondes:', err);
                } finally {
                    setTrLoadingRounds(false);
                }
            } else {
                setTrRounds([]);
                setTrSelectedRound('');
            }
        };
        fetchTrRounds();
    }, [auction?.season, trSelectedStage, trStages]);

    useEffect(() => {
        if (!auctionId) return;

        let pathSegments;
        if (selectedRound === 'total') {
            pathSegments = ['auctions', auctionId, 'ranking', 'liveRanking'];
        } else if (selectedRound === 'finalRankingPoule') {
            pathSegments = ['auctions', auctionId, 'ranking', 'finalRankingPoule'];
        } else if (selectedRound === 'liveRankingIntermediateRound') {
            pathSegments = ['auctions', auctionId, 'ranking', 'liveRankingIntermediateRound'];
        } else if (selectedRound === 'liveRankingFinaleronde') {
            pathSegments = ['auctions', auctionId, 'ranking', 'liveRankingFinaleronde'];
        } else {
            pathSegments = ['auctions', auctionId, 'roundRankings', selectedRound];
        }

        const finalPath = pathSegments.join('/');
        const rankingRef = doc(db, finalPath);

        const unsubRanking = onSnapshot(rankingRef, (docSnap) => {
            if (docSnap.exists()) {
                let data;
                if (selectedRound === 'total' || selectedRound === 'finalRankingPoule' || selectedRound === 'liveRankingIntermediateRound' || selectedRound === 'liveRankingFinaleronde') {
                    data = docSnap.data().ranking || [];
                    if (selectedRound === 'liveRankingFinaleronde' && finalRoundParticipants?.participantUids?.length) {
                        const finalistSet = new Set(finalRoundParticipants.participantUids);
                        data = data.filter(row => finalistSet.has(row.uid));
                    }
                } else {
                    const roundData = docSnap.data();
                    data = (roundData.results || []).sort((a, b) => {
                        if (b.matchPoints !== a.matchPoints) {
                            return b.matchPoints - a.matchPoints;
                        }
                        const aScorePoints = (a.playerPoints || 0) + (a.tacticPoints || 0) + (a.tacticCounter || 0);
                        const bScorePoints = (b.playerPoints || 0) + (b.tacticPoints || 0) + (b.tacticCounter || 0);
                        return bScorePoints - aScorePoints;
                    });
                }
                setRankings(data);
            } else {
                setRankings([]);
            }
        }, (err) => {
            console.error("Error fetching live ranking:", err);
        });

        return () => unsubRanking();
    }, [auctionId, selectedRound, finalRoundParticipants]);

    // Update default selectedRound based on spelfase - alleen bij spelfase wijziging
    useEffect(() => {
        if (auction?.spelfase === 'Tussenronde' && liveRankingIntermediateRound) {
            setSelectedRound('liveRankingIntermediateRound');
        } else if (auction?.spelfase === 'Finaleronde' && (liveRankingFinaleronde || finalRoundParticipants)) {
            setSelectedRound('liveRankingFinaleronde');
        } else if (auction?.spelfase === 'Tussenronde' && !liveRankingIntermediateRound) {
            if (finalRankingPoule) {
                setSelectedRound('finalRankingPoule');
            }
        } else if (auction?.spelfase && !['Tussenronde', 'Finaleronde'].includes(auction.spelfase)) {
            setSelectedRound('total');
        }
    }, [auction?.spelfase, liveRankingIntermediateRound, finalRankingPoule, liveRankingFinaleronde, finalRoundParticipants]);

    useEffect(() => {
        const fetchRounds = async () => {
            if (auction?.season) {
                try {
                    const getRoundsForSeason = httpsCallable(functions, 'getRoundsForSeason');
                    const result = await getRoundsForSeason({ seasonId: auction.season });
                    const roundsData = (result.data || []).sort((a, b) => (a.starting_at || '').localeCompare(b.starting_at || ''));
                    setSportmonksRounds(roundsData);
                } catch (err) {
                    console.error("Error fetching Sportmonks rounds:", err);
                    setError('Fout bij het ophalen van speelrondes van Sportmonks.');
                }
            }
        };

        fetchRounds();
    }, [auction]);

    const participantNameToUidMap = useMemo(() => {
        const map = new Map();
        participants.forEach(p => {
            map.set(p.name, p.id);
        });
        return map;
    }, [participants]);

    const participantsByName = useMemo(() => {
        const map = new Map();
        participants.forEach(p => {
            map.set(p.name, p);
        });
        return map;
    }, [participants]);

    const playerIdToNameMap = useMemo(() => {
        const map = new Map();
        auctionPlayers.forEach(p => map.set(p.id, p.name));
        return map;
    }, [auctionPlayers]);

    const tacticsMap = useMemo(() => new Map([
        ['totaalvoetbal', 'Totaalvoetbal'],
        ['barcelona', 'Barcelona'],
        ['mourinho', 'Mourinho\'s Bus'],
        ['catenaccio', 'Catenaccio'],
    ]), []);

    const closedRounds = useMemo(() => {
        if (!schedule) return [];
        return Object.entries(schedule)
            .filter(([roundId, roundData]) => 
                roundData.status === 'closed' && 
                !roundId.startsWith('intermediateRound') &&
                roundData.type !== 'knockout'
            )
            .map(([roundId, roundData]) => ({ id: roundId, ...roundData }))
            .sort((a, b) => {
                const roundNumA = parseInt(a.id.replace('round', ''));
                const roundNumB = parseInt(b.id.replace('round', ''));
                return roundNumA - roundNumB;
            });
    }, [schedule]);

    const sortedSchedule = useMemo(() => {
        if (!schedule) return [];
        return Object.entries(schedule).sort(([keyA], [keyB]) => {
            const numA = parseInt(keyA.replace('round', ''), 10);
            const numB = parseInt(keyB.replace('round', ''), 10);
            return numA - numB;
        });
    }, [schedule]);

    // Gefilterde poulefase rondes (exclusief alle knockout rondes)
    const pouleRounds = useMemo(() => {
        return sortedSchedule.filter(([roundId, roundData]) => {
            // Exclude alle knockout rondes (tussenronde, achtste finale, etc.)
            if (roundData.type === 'knockout') {
                return false;
            }
            // Backward compatibility: exclude old intermediateRound naming
            if (roundId.startsWith('intermediateRound')) {
                return false;
            }
            return true;
        });
    }, [sortedSchedule]);

    // Gefilterde tussenronde rondes
    const intermediateRounds = useMemo(() => {
        return sortedSchedule.filter(([roundId, roundData]) => {
            // New format: knockout rounds with stageName 'Knockout Round Play-offs'
            if (roundData.type === 'knockout' && roundData.stageName === 'Knockout Round Play-offs') {
                return true;
            }
            // Backward compatibility: old intermediateRound naming
            if (roundId.startsWith('intermediateRound')) {
                return true;
            }
            return false;
        });
    }, [sortedSchedule]);

    // Gefilterde knock-out achtste finale rondes
    const knockoutAchtsteRounds = useMemo(() => {
        return sortedSchedule.filter(([roundId, roundData]) => {
            return roundData.type === 'knockout' && roundData.stageName === '8th Finals';
        }).sort(([, a], [, b]) => {
            // Sorteer op leg: 1/2 voor 2/2
            return (a.leg || '').localeCompare(b.leg || '');
        });
    }, [sortedSchedule]);

    // Gefilterde finale rondes (Quarter-finals, Semi-finals, Final)
    const finalRounds = useMemo(() => {
        return Object.fromEntries(
            sortedSchedule.filter(([roundId, roundData]) => {
                return roundData.type === 'knockout' && ['Quarter-finals', 'Semi-finals', 'Final'].includes(roundData.stageName);
            })
        );
    }, [sortedSchedule]);

    // Bepaal of de finaleronde is verwerkt (de 'Final' ronde is gesloten)
    const isFinaleProcessed = useMemo(() => {
        const finalRound = Object.values(finalRounds).find(roundData => roundData.stageName === 'Final');
        return finalRound?.status === 'closed';
    }, [finalRounds]);

    // Tel per deelnemer het aantal speelronde-overwinningen (1e plek) in de poulefase
    useEffect(() => {
        if (!auctionId || closedRounds.length === 0) {
            setPouleWinCounts({});
            return;
        }
        let cancelled = false;
        const scoreOf = (r) => (r.playerPoints || 0) + (r.tacticPoints || 0) + (r.tacticCounter || 0);
        const fetchWinners = async () => {
            const counts = {};
            await Promise.all(closedRounds.map(async (round) => {
                const snap = await getDoc(doc(db, 'auctions', auctionId, 'roundRankings', round.id));
                if (!snap.exists()) return;
                const results = snap.data().results || [];
                if (results.length === 0) return;
                const winner = [...results].sort((a, b) => {
                    if ((b.matchPoints || 0) !== (a.matchPoints || 0)) {
                        return (b.matchPoints || 0) - (a.matchPoints || 0);
                    }
                    return scoreOf(b) - scoreOf(a);
                })[0];
                if (winner?.uid) {
                    counts[winner.uid] = (counts[winner.uid] || 0) + 1;
                }
            }));
            if (!cancelled) setPouleWinCounts(counts);
        };
        fetchWinners();
        return () => { cancelled = true; };
    }, [auctionId, closedRounds]);

    const firstNonClosedRoundIndex = useMemo(() => {
        if (pouleRounds.length === 0) return 0;
        const firstIndex = pouleRounds.findIndex(([, roundData]) => roundData.status !== 'closed');
        return firstIndex === -1 ? 0 : firstIndex; // Fallback to first round if all are closed
    }, [pouleRounds]);

    // Calculate all match scores when data changes
    useEffect(() => {
        const calculateAllScores = async () => {
            if (!schedule || Object.keys(schedule).length === 0 || !auction?.season || participants.length === 0) {
                setMatchScores({});
                return;
            }

            // 1. Fetch all data in bulk
            const transfersQuery = query(collection(db, 'auctions', auctionId, 'auctionTransfers'), orderBy('timestamp', 'asc'));
            const transfersSnap = await getDocs(transfersQuery);

            const allPlayerScores = new Map(); // Map<roundId, Map<playerId, score[]>>
            const scorePromises = Object.values(schedule)
                .filter(round => round.status !== 'closed' && round.roundId)
                .map(round => {
                    const scoresQuery = collection(db, `playerScores/${auction.season}/rounds/${round.roundId}/scores`);
                    return getDocs(scoresQuery).then(snapshot => {
                        const scoresByPlayer = new Map();
                        snapshot.forEach(doc => {
                            const scoreData = doc.data();
                            const playerId = String(scoreData.playerId);
                            if (!scoresByPlayer.has(playerId)) {
                                scoresByPlayer.set(playerId, []);
                            }
                            scoresByPlayer.get(playerId).push(scoreData);
                        });
                        allPlayerScores.set(round.roundId, scoresByPlayer);
                    });
                });

            const allRoundRankings = new Map(); // Map<roundKey, results[]>
            const rankingPromises = Object.entries(schedule)
                .filter(([, round]) => round.status === 'closed')
                .map(([roundKey]) => {
                    const roundRankingRef = doc(db, 'auctions', auctionId, 'roundRankings', roundKey);
                    return getDoc(roundRankingRef).then(roundRankingSnap => {
                        if (roundRankingSnap.exists()) {
                            allRoundRankings.set(roundKey, roundRankingSnap.data().results || []);
                        }
                    });
                });

            await Promise.all([...scorePromises, ...rankingPromises]);

            // 2. Pre-calculate team compositions
            const teamCompositions = new Map(); // Map<participantId, Set<playerId>>
            participants.forEach(p => teamCompositions.set(p.id, new Set()));
            transfersSnap.docs.forEach(doc => {
                const transfer = doc.data();
                if (teamCompositions.has(transfer.to)) teamCompositions.get(transfer.to).add(String(transfer.playerId));
                if (teamCompositions.has(transfer.from)) teamCompositions.get(transfer.from).delete(String(transfer.playerId));
            });

            // 3. Loop through schedule and calculate scores
            const newScores = {};
            for (const [roundKey, roundData] of Object.entries(schedule)) {
                if (!roundData.matches) continue;

                for (const [matchIndex, match] of roundData.matches.entries()) {
                    const matchId = `${roundKey}-${matchIndex}`;
                    const homeParticipant = participants.find(p => p.name === match.home);
                    const awayParticipant = participants.find(p => p.name === match.away);

                    if (!homeParticipant || !awayParticipant) {
                        newScores[matchId] = { homeScore: 0, awayScore: 0 };
                        continue;
                    }

                    let homeScore = 0;
                    let awayScore = 0;

                    if (roundData.status === 'closed') {
                        const roundRanking = allRoundRankings.get(roundKey);
                        if (roundRanking) {
                            const homeResult = roundRanking.find(r => r.uid === homeParticipant.id);
                            const awayResult = roundRanking.find(r => r.uid === awayParticipant.id);
                            if (homeResult) {
                                homeScore = (homeResult.playerPoints || 0) + (homeResult.tacticPoints || 0) + (homeResult.tacticCounter || 0);
                            }
                            if (awayResult) {
                                awayScore = (awayResult.playerPoints || 0) + (awayResult.tacticPoints || 0) + (awayResult.tacticCounter || 0);
                            }
                        }
                    } else if (roundData.roundId) {
                        const playerScoresForRound = allPlayerScores.get(roundData.roundId);
                        if (playerScoresForRound) {
                            const homeTeamPlayers = Array.from(teamCompositions.get(homeParticipant.id) || []);
                            const awayTeamPlayers = Array.from(teamCompositions.get(awayParticipant.id) || []);

                            const homeSelection = matchSelections[homeParticipant.id]?.[matchId] || {};
                            const awaySelection = matchSelections[awayParticipant.id]?.[matchId] || {};

                            homeTeamPlayers.forEach(playerId => {
                                const scoresArray = playerScoresForRound.get(playerId) || [];
                                const isCaptain = playerId === String(homeSelection.captain);

                                if (isCaptain) {
                                    let totalPoints = 0;
                                    let totalBonusPoints = 0;
                                    scoresArray.forEach(score => {
                                        const bonusPointsToExclude = [
                                            "defender_bonus",
                                            "spielmacher_bonus",
                                            "save_bonus",
                                            "interceptions_bonus",
                                        ];
                                        let bonusPoints = 0;
                                        const breakdown = score.breakdown || {};

                                        for (const bonus of bonusPointsToExclude) {
                                            if (breakdown[bonus]) {
                                                bonusPoints += breakdown[bonus].points;
                                            }
                                        }
                                        totalPoints += score.totalPoints || 0;
                                        totalBonusPoints += bonusPoints;
                                    });
                                    const basePoints = totalPoints - totalBonusPoints;
                                    homeScore += (basePoints * 1.5) + totalBonusPoints;
                                } else {
                                    const totalPlayerScore = scoresArray.reduce((sum, score) => sum + (score.totalPoints || 0), 0);
                                    homeScore += totalPlayerScore;
                                }
                            });

                            awayTeamPlayers.forEach(playerId => {
                                const scoresArray = playerScoresForRound.get(playerId) || [];
                                const isCaptain = playerId === String(awaySelection.captain);

                                if (isCaptain) {
                                    let totalPoints = 0;
                                    let totalBonusPoints = 0;
                                    scoresArray.forEach(score => {
                                        const bonusPointsToExclude = [
                                            "defender_bonus",
                                            "spielmacher_bonus",
                                            "save_bonus",
                                            "interceptions_bonus",
                                        ];
                                        let bonusPoints = 0;
                                        const breakdown = score.breakdown || {};

                                        for (const bonus of bonusPointsToExclude) {
                                            if (breakdown[bonus]) {
                                                bonusPoints += breakdown[bonus].points;
                                            }
                                        }
                                        totalPoints += score.totalPoints || 0;
                                        totalBonusPoints += bonusPoints;
                                    });
                                    const basePoints = totalPoints - totalBonusPoints;
                                    awayScore += (basePoints * 1.5) + totalBonusPoints;
                                } else {
                                    const totalPlayerScore = scoresArray.reduce((sum, score) => sum + (score.totalPoints || 0), 0);
                                    awayScore += totalPlayerScore;
                                }
                            });
                        }
                    }
                    newScores[matchId] = { homeScore, awayScore };
                }
            }
            setMatchScores(newScores);
        };

        calculateAllScores();
    }, [schedule, auction, participants, matchSelections, auctionId]);

    // Persist showTacticsAndCaptains to localStorage
    useEffect(() => {
        if (auctionId) {
            localStorage.setItem(`adminShowTacticsAndCaptains_${auctionId}`, JSON.stringify(showTacticsAndCaptains));
        }
    }, [showTacticsAndCaptains, auctionId]);

    const handleParticipantClick = (participantName, roundId, roundStatus, matchId) => {
        const participant = participantsByName.get(participantName);
        if (participant) {
            setSelectedParticipantForModal(participant);
            setModalRoundId(roundId);
            setModalRoundStatus(roundStatus);
            setModalMatchId(matchId);
            setIsParticipantModalOpen(true);
        }
    };

    const handleCloseParticipantModal = () => {
        setIsParticipantModalOpen(false);
        setSelectedParticipantForModal(null);
        setModalRoundId(null);
        setModalRoundStatus(null);
        setModalMatchId(null);
    };

    const handleRankingRowClick = (participant) => {
        const fullParticipant = participants.find(p => p.uid === participant.uid);
        if (fullParticipant) {
            setSelectedParticipantForModal(fullParticipant);
            setIsParticipantModalOpen(true);
            // For rankings, we don't have a specific round, so we'll use the first available round
            if (schedule && Object.keys(schedule).length > 0) {
                const firstRound = Object.values(schedule)[0];
                setModalRoundId(firstRound.roundId);
                setModalRoundStatus(firstRound.status);
            }
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleLoting = async () => {
        setConfirmLotingOpen(false);

        // Check if current spelfase allows creating loting
        const allowedPhases = ['Voorbereiding', 'Veiling'];
        if (!allowedPhases.includes(auction?.spelfase)) {
            setError("In deze spelfase mag geen nieuwe loting worden uitgevoerd. Dit kan alleen in de spelfases Voorbereiding en Veiling.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (participants.length < 2) {
                setError("Er zijn minimaal 2 deelnemers nodig voor een loting.");
                setLoading(false);
                return;
            }

            const numRounds = 8;
            const groupStageRounds = sportmonksRounds.filter(r =>
                r.stage?.name === 'Group Stage' || r.stage?.name === 'League Stage'
            );

            if (groupStageRounds.length < numRounds) {
                setError(`Niet genoeg speelrondes (verwacht ${numRounds}, gevonden ${groupStageRounds.length}) in de groepsfase van Sportmonks. Controleer de data.`);
                setLoading(false);
                return;
            }

            let teams = [...participants].sort(() => Math.random() - 0.5);

            if (teams.length % 2 !== 0) {
                teams.push({ name: 'Vrij', uid: 'bye' });
            }

            const numTeams = teams.length;
            const finalSchedule = {};
            const firstTeam = teams.shift();

            for (let roundNum = 0; roundNum < numRounds; roundNum++) {
                const roundKey = `round${roundNum + 1}`;
                const sportmonksRound = groupStageRounds[roundNum];
                finalSchedule[roundKey] = {
                    matches: [],
                    roundId: sportmonksRound?.id
                };

                let match = { home: firstTeam.name, away: teams[0].name };
                if (Math.random() > 0.5) {
                    [match.home, match.away] = [match.away, match.home];
                }
                finalSchedule[roundKey].matches.push(match);

                for (let i = 1; i < numTeams / 2; i++) {
                    let match = { home: teams[i].name, away: teams[numTeams - 1 - i].name };
                    if (Math.random() > 0.5) {
                        [match.home, match.away] = [match.away, match.home];
                    }
                    finalSchedule[roundKey].matches.push(match);
                }

                teams.unshift(teams.pop());
            }

            const batch = writeBatch(db);
            const scheduleCollectionRef = collection(db, 'auctions', auctionId, 'schedule');
            const oldScheduleSnap = await getDocs(scheduleCollectionRef);
            oldScheduleSnap.forEach(doc => batch.delete(doc.ref));

            Object.entries(finalSchedule).forEach(([roundId, roundData]) => {
                const validMatches = roundData.matches.filter(m => m.home !== 'Vrij' && m.away !== 'Vrij');
                if (validMatches.length > 0) {
                    const roundDocRef = doc(scheduleCollectionRef, roundId);
                    batch.set(roundDocRef, { matches: validMatches, status: 'open', roundId: roundData.roundId });
                }
            });

            await batch.commit();
        } catch (err) {
            console.error("Error during loting:", err);
            setError("Er is een fout opgetreden bij het genereren van de loting.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseRound = async (roundId) => {
        setConfirmAction({ open: false, type: null, roundId: null, roundData: null, title: '', content: '' }); // Close dialog first
        setLoading(true);
        setError(null);
        try {
            // Pre-flight check: zorg dat er scores bestaan voor de bijbehorende opslag-ID.
            // Poulefase gebruikt het Sportmonks round-ID (roundData.roundId); knockout/finale
            // rondes hebben roundId=null en gebruiken de schedule-doc key (bv. "77480823-1")
            // als opslag-ID. Zelfde fallback als de cloud function onRoundCloseUpdateRanking.
            const roundData = schedule[roundId];
            const sportmonksRoundId = roundData?.roundId || roundId;
            if (!sportmonksRoundId) {
                throw new Error("Sportmonks round ID niet gevonden in de ronde.");
            }

            const scoresCollectionRef = collection(db, `playerScores/${auction.season}/rounds/${sportmonksRoundId}/scores`);
            const scoresSnapshot = await getDocs(scoresCollectionRef);

            if (scoresSnapshot.empty) {
                throw new Error(`Geen spelersscores gevonden voor ronde ${sportmonksRoundId}. Bereken eerst de punten voor deze ronde.`);
            }

            // Just update the status, the cloud function will do the rest
            const scheduleDocRef = doc(db, 'auctions', auctionId, 'schedule', roundId);
            await setDoc(scheduleDocRef, { status: 'closed' }, { merge: true });

        } catch (err) {
            console.error("Error closing round:", err);
            setError(`Fout bij het sluiten van de speelronde: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const copyTacticsAndCaptainsFromPreviousRound = async (currentRoundId, currentRoundData) => {
        try {
            // Find the previous round
            const roundNumber = parseInt(currentRoundId.replace('round', ''));
            const previousRoundId = `round${roundNumber - 1}`;

            // Check if previous round exists
            if (roundNumber <= 1) {
                return; // No previous round to copy from
            }

            const previousRoundData = schedule[previousRoundId];
            if (!previousRoundData || !previousRoundData.matches) {
                return; // Previous round doesn't exist or has no matches
            }

            const batch = writeBatch(db);
            let hasUpdates = false;

            // Process each match in the current round
            for (const [matchIndex, currentMatch] of currentRoundData.matches.entries()) {
                const currentMatchId = `${currentRoundId}-${matchIndex}`;

                // Find corresponding match in previous round (same teams)
                const previousMatch = previousRoundData.matches.find(prevMatch =>
                    (prevMatch.home === currentMatch.home && prevMatch.away === currentMatch.away) ||
                    (prevMatch.home === currentMatch.away && prevMatch.away === currentMatch.home)
                );

                if (!previousMatch) continue;

                const previousMatchId = `${previousRoundId}-${previousRoundData.matches.indexOf(previousMatch)}`;

                // For each participant in the current match
                const homeUid = participantNameToUidMap.get(currentMatch.home);
                const awayUid = participantNameToUidMap.get(currentMatch.away);

                if (homeUid) {
                    const currentHomeSelection = matchSelections[homeUid]?.[currentMatchId] || {};
                    const previousHomeSelection = matchSelections[homeUid]?.[previousMatchId] || {};

                    // Copy captain if not set
                    if (!currentHomeSelection.captain && previousHomeSelection.captain) {
                        const homeSelectionRef = doc(db, 'auctions', auctionId, 'matchSelections', homeUid);
                        const currentSelections = matchSelections[homeUid] || {};
                        batch.set(homeSelectionRef, {
                            selections: {
                                ...currentSelections,
                                [currentMatchId]: {
                                    ...currentHomeSelection,
                                    captain: previousHomeSelection.captain
                                }
                            }
                        }, { merge: true });
                        hasUpdates = true;
                    }

                    // Copy tactic if not set
                    if (!currentHomeSelection.tactic && previousHomeSelection.tactic) {
                        const homeSelectionRef = doc(db, 'auctions', auctionId, 'matchSelections', homeUid);
                        const currentSelections = matchSelections[homeUid] || {};
                        batch.set(homeSelectionRef, {
                            selections: {
                                ...currentSelections,
                                [currentMatchId]: {
                                    ...currentHomeSelection,
                                    tactic: previousHomeSelection.tactic
                                }
                            }
                        }, { merge: true });
                        hasUpdates = true;
                    }
                }

                if (awayUid) {
                    const currentAwaySelection = matchSelections[awayUid]?.[currentMatchId] || {};
                    const previousAwaySelection = matchSelections[awayUid]?.[previousMatchId] || {};

                    // Copy captain if not set
                    if (!currentAwaySelection.captain && previousAwaySelection.captain) {
                        const awaySelectionRef = doc(db, 'auctions', auctionId, 'matchSelections', awayUid);
                        const currentSelections = matchSelections[awayUid] || {};
                        batch.set(awaySelectionRef, {
                            selections: {
                                ...currentSelections,
                                [currentMatchId]: {
                                    ...currentAwaySelection,
                                    captain: previousAwaySelection.captain
                                }
                            }
                        }, { merge: true });
                        hasUpdates = true;
                    }

                    // Copy tactic if not set
                    if (!currentAwaySelection.tactic && previousAwaySelection.tactic) {
                        const awaySelectionRef = doc(db, 'auctions', auctionId, 'matchSelections', awayUid);
                        const currentSelections = matchSelections[awayUid] || {};
                        batch.set(awaySelectionRef, {
                            selections: {
                                ...currentSelections,
                                [currentMatchId]: {
                                    ...currentAwaySelection,
                                    tactic: previousAwaySelection.tactic
                                }
                            }
                        }, { merge: true });
                        hasUpdates = true;
                    }
                }
            }

            if (hasUpdates) {
                await batch.commit();
                console.log(`Tactiek en aanvoerders gekopieerd van ${previousRoundId} naar ${currentRoundId}`);
            }

        } catch (err) {
            console.error("Error copying tactics and captains from previous round:", err);
            // Don't throw error, just log it - this shouldn't prevent the round from starting
        }
    };

    const handleToggleRoundStatus = async (roundId, roundData) => {
        // This function now only handles STARTING a round, via the confirmation dialog.
        // Opening a 'running' round back to 'open' is not a standard operation here.
        setConfirmAction({ open: false, type: null, roundId: null, roundData: null, title: '', content: '' }); // Close dialog first
        setLoading(true);
        setError(null);
        try {
            // Skip copying tactics for intermediate rounds (they don't have matches)
            if (!roundId.startsWith('intermediateRound')) {
                await copyTacticsAndCaptainsFromPreviousRound(roundId, roundData);
            }

            const scheduleDocRef = doc(db, 'auctions', auctionId, 'schedule', roundId);
            await setDoc(scheduleDocRef, { ...roundData, status: 'running' }, { merge: true });
        } catch (err) {
            console.error("Error starting round:", err);
            setError(`Fout bij het starten van de ronde: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleReopenRound = async (roundId, roundData) => {
        setConfirmAction({ open: false, type: null, roundId: null, roundData: null, title: '', content: '' }); // Close dialog first
        setLoading(true);
        setError(null);
        try {
            // Knockout/finale rondes hebben roundId=null; val terug op de schedule-doc key
            // als opslag-ID (gelijk aan handleCloseRound en de cloud function).
            const sportmonksRoundId = roundData.roundId || roundId;
            if (!sportmonksRoundId) {
                throw new Error("Sportmonks round ID niet gevonden in de ronde.");
            }

            const batch = writeBatch(db);

            // 1. Delete the round ranking document. This triggers a cloud function to update the total ranking.
            const roundRankingDocRef = doc(db, 'auctions', auctionId, 'roundRankings', roundId);
            batch.delete(roundRankingDocRef);

            // 2. Delete the player-per-round records for this round.
            const playersPerRoundQuery = query(
                collection(db, 'auctions', auctionId, 'playersPerParticipantPerRound'),
                where('roundId', '==', sportmonksRoundId)
            );
            const playersPerRoundSnapshot = await getDocs(playersPerRoundQuery);
            playersPerRoundSnapshot.forEach(doc => {
                batch.delete(doc.ref);
            });

            // 3. Set the schedule status back to 'open'.
            const scheduleDocRef = doc(db, 'auctions', auctionId, 'schedule', roundId);
            batch.update(scheduleDocRef, { status: 'open' });

            await batch.commit();

        } catch (err) {
            console.error("Error reopening round:", err);
            setError(`Fout bij het heropenen van de speelronde: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRevertToOpen = async (roundId) => {
        setConfirmAction({ open: false, type: null, roundId: null, roundData: null, title: '', content: '' }); // Close dialog first
        setLoading(true);
        setError(null);
        try {
            const scheduleDocRef = doc(db, 'auctions', auctionId, 'schedule', roundId);
            await setDoc(scheduleDocRef, { status: 'open' }, { merge: true });
        } catch (err) {
            console.error("Error reverting round to open:", err);
            setError(`Fout bij het terugzetten van de ronde: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleActionConfirmation = () => {
        const { type, roundId, roundData } = confirmAction;
        if (type === 'start') {
            handleToggleRoundStatus(roundId, roundData);
        } else if (type === 'close') {
            handleCloseRound(roundId);
        } else if (type === 'reopen') {
            handleReopenRound(roundId, roundData);
        } else if (type === 'revert_to_open') {
            handleRevertToOpen(roundId);
        }
    };

    const openConfirmationDialog = (type, roundId, roundData) => {
        let title = '';
        let content = '';
        const isIntermediate = roundId.startsWith('intermediateRound');
        const roundLabel = isIntermediate 
            ? `Tussenronde ${roundId.replace('intermediateRound', '')}` 
            : `Speelronde ${roundId.replace('round', '')}`;
        
        switch (type) {
            case 'start':
                title = `${roundLabel} Starten?`;
                content = isIntermediate
                    ? 'Weet je zeker dat je deze tussenronde wilt starten? Deelnemers kunnen hun aanvoerder hierna niet meer wijzigen.'
                    : 'Weet je zeker dat je deze speelronde wilt starten? Deelnemers kunnen hun tactiek en aanvoerder hierna niet meer wijzigen.';
                break;
            case 'close':
                title = `${roundLabel} Sluiten?`;
                content = isIntermediate
                    ? 'Weet je zeker dat je deze tussenronde wilt sluiten? De punten worden definitief berekend en de tussenronde ranglijst wordt bijgewerkt.'
                    : 'Weet je zeker dat je deze speelronde wilt sluiten? De punten worden definitief berekend en de ranglijst wordt bijgewerkt.';
                break;
            case 'reopen':
                title = `${roundLabel} Heropenen?`;
                content = isIntermediate
                    ? 'Weet je zeker dat je deze tussenronde wilt heropenen? LET OP: De berekende scores voor deze ronde worden permanent verwijderd.'
                    : 'Weet je zeker dat je deze speelronde wilt heropenen? LET OP: De berekende scores voor deze ronde worden permanent verwijderd. Dit kan onherstelbare gevolgen hebben voor de puntentelling als dit niet correct wordt beheerd.';
                break;
            case 'revert_to_open':
                title = `${roundLabel} Terugzetten?`;
                content = isIntermediate
                    ? 'Weet je zeker dat je deze tussenronde wilt terugzetten naar "open"? Deelnemers kunnen hun aanvoerder dan weer wijzigen.'
                    : 'Weet je zeker dat je deze speelronde wilt terugzetten naar "open"? Deelnemers kunnen hun tactiek en aanvoerder dan weer wijzigen.';
                break;
            default:
                return;
        }
        setConfirmAction({ open: true, type, roundId, roundData, title, content });
    };


    const handleDeleteLoting = async () => {
        setConfirmDeleteLotingOpen(false);

        // Check if current spelfase allows deleting loting
        const allowedPhases = ['Voorbereiding', 'Veiling'];
        if (!allowedPhases.includes(auction?.spelfase)) {
            setError("In deze spelfase mag de loting niet verwijderd worden. Dit kan alleen in de spelfases Voorbereiding en Veiling.");
            return;
        }

        setLoading(true);
        try {
            const scheduleCollectionRef = collection(db, 'auctions', auctionId, 'schedule');
            const oldScheduleSnap = await getDocs(scheduleCollectionRef);
            const batch = writeBatch(db);
            oldScheduleSnap.forEach(doc => batch.delete(doc.ref));
            await batch.commit();
        } catch (err) {
            console.error("Error deleting schedule:", err);
            setError("Fout bij het verwijderen van de loting.");
        } finally {
            setLoading(false);
        }
    };

    const handleClosePoulePhase = async () => {
        setConfirmClosePouleOpen(false);
        setLoading(true);
        setError(null);

        try {
            // Haal de huidige liveRanking op
            const liveRankingRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRanking');
            const liveRankingSnap = await getDoc(liveRankingRef);

            if (!liveRankingSnap.exists()) {
                setError("Geen liveRanking gevonden om op te slaan.");
                setLoading(false);
                return;
            }

            const liveRankingData = liveRankingSnap.data();

            // Sla de liveRanking op als finalRankingPoule
            const finalRankingPouleRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRankingPoule');
            await setDoc(finalRankingPouleRef, {
                ...liveRankingData,
                closedAt: serverTimestamp()
            });

            // Reset de liveRanking - zet alle deelnemers op 0 punten
            const resetRanking = (liveRankingData.ranking || []).map(participant => ({
                uid: participant.uid,
                name: participant.name,
                matchPoints: 0,
                playerPoints: 0,
                tacticPoints: 0,
                tacticCounter: 0,
                wins: 0,
                draws: 0,
                losses: 0
            }));

            await setDoc(liveRankingRef, {
                ranking: resetRanking,
                lastUpdated: serverTimestamp()
            });

        } catch (err) {
            console.error("Error closing poule phase:", err);
            setError("Fout bij het afsluiten van de poulefase.");
        } finally {
            setLoading(false);
        }
    };

    const handleReopenPoulePhase = async () => {
        setConfirmReopenPouleOpen(false);
        setLoading(true);
        setError(null);

        try {
            // Haal de finalRankingPoule op
            const finalRankingPouleRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRankingPoule');
            const finalRankingPouleSnap = await getDoc(finalRankingPouleRef);

            if (!finalRankingPouleSnap.exists()) {
                setError("Geen finalRankingPoule gevonden om te herstellen.");
                setLoading(false);
                return;
            }

            const finalRankingPouleData = finalRankingPouleSnap.data();

            // Herstel de liveRanking met de data uit finalRankingPoule
            const liveRankingRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRanking');
            await setDoc(liveRankingRef, {
                ranking: finalRankingPouleData.ranking || [],
                lastUpdated: serverTimestamp()
            });

            // Verwijder het finalRankingPoule document
            const batch = writeBatch(db);
            batch.delete(finalRankingPouleRef);
            await batch.commit();

        } catch (err) {
            console.error("Error reopening poule phase:", err);
            setError("Fout bij het heropenen van de poulefase.");
        } finally {
            setLoading(false);
        }
    };

    const handleStartIntermediateRound = async () => {
        setConfirmStartIntermediateOpen(false);
        setLoading(true);
        setError(null);

        try {
            // Haal de finalRankingPoule op
            const finalRankingPouleRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRankingPoule');
            const finalRankingPouleSnap = await getDoc(finalRankingPouleRef);

            if (!finalRankingPouleSnap.exists()) {
                setError("Geen finalRankingPoule gevonden. Sluit eerst de poulefase af.");
                setLoading(false);
                return;
            }

            const finalRankingPouleData = finalRankingPouleSnap.data();
            const allParticipants = finalRankingPouleData.ranking || [];

            // Sorteer op matchPoints (en daarna scorePoints) om top 15 te bepalen
            const sortedParticipants = [...allParticipants].sort((a, b) => {
                if (b.matchPoints !== a.matchPoints) {
                    return b.matchPoints - a.matchPoints;
                }
                const aScorePoints = (a.playerPoints || 0) + (a.tacticPoints || 0) + (a.tacticCounter || 0);
                const bScorePoints = (b.playerPoints || 0) + (b.tacticPoints || 0) + (b.tacticCounter || 0);
                return bScorePoints - aScorePoints;
            });

            // Neem alle deelnemers behalve de top 15
            const participantsForIntermediate = sortedParticipants.slice(15);

            // Maak een nieuwe ranking met 0 punten voor alle deelnemers
            const intermediateRanking = participantsForIntermediate.map(participant => ({
                uid: participant.uid,
                name: participant.name,
                matchPoints: 0,
                playerPoints: 0,
                tacticPoints: 0,
                tacticCounter: 0,
                wins: 0,
                draws: 0,
                losses: 0
            }));

            // Sla de liveRankingIntermediateRound op
            const liveRankingIntermediateRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingIntermediateRound');
            await setDoc(liveRankingIntermediateRef, {
                ranking: intermediateRanking,
                createdAt: serverTimestamp(),
                lastUpdated: serverTimestamp()
            });

            // Haal de Knockout Round Play-offs stage info uit de geïmporteerde schedule
            const seasonId = auction?.season;
            if (!seasonId) {
                setError("Geen seizoen gevonden voor deze veiling.");
                setLoading(false);
                return;
            }

            // Zoek de Knockout Round Play-offs stage in de schedule collectie
            const scheduleRoundsRef = collection(db, 'schedule', String(seasonId), 'rounds');
            const scheduleRoundsSnapshot = await getDocs(scheduleRoundsRef);
            
            let knockoutPlayoffsRounds = [];
            scheduleRoundsSnapshot.docs.forEach(docSnap => {
                const data = docSnap.data();
                if (data.stageName === 'Knockout Round Play-offs' && data.type === 'knockout') {
                    knockoutPlayoffsRounds.push({
                        docId: docSnap.id,
                        ...data
                    });
                }
            });

            // Sorteer op leg (1/2 voor 2/2)
            knockoutPlayoffsRounds.sort((a, b) => {
                const legA = a.leg || '';
                const legB = b.leg || '';
                return legA.localeCompare(legB);
            });

            if (knockoutPlayoffsRounds.length < 2) {
                // Fallback: gebruik oude naming als schedule niet geïmporteerd is
                console.warn("Knockout Round Play-offs niet gevonden in schedule, gebruik fallback naming.");
                const intermediateRound1Ref = doc(db, 'auctions', auctionId, 'schedule', 'intermediateRound1');
                const intermediateRound2Ref = doc(db, 'auctions', auctionId, 'schedule', 'intermediateRound2');
                
                await setDoc(intermediateRound1Ref, {
                    roundId: '',
                    status: 'open'
                });
                
                await setDoc(intermediateRound2Ref, {
                    roundId: '',
                    status: 'open'
                });
            } else {
                // Gebruik de juiste IDs uit de schedule
                const round1Data = knockoutPlayoffsRounds[0];
                const round2Data = knockoutPlayoffsRounds[1];

                const intermediateRound1Ref = doc(db, 'auctions', auctionId, 'schedule', round1Data.docId);
                const intermediateRound2Ref = doc(db, 'auctions', auctionId, 'schedule', round2Data.docId);
                
                await setDoc(intermediateRound1Ref, {
                    roundId: round1Data.docId,
                    stageId: round1Data.stageId,
                    stageName: round1Data.stageName,
                    leg: round1Data.leg,
                    type: round1Data.type,
                    status: 'open'
                });
                
                await setDoc(intermediateRound2Ref, {
                    roundId: round2Data.docId,
                    stageId: round2Data.stageId,
                    stageName: round2Data.stageName,
                    leg: round2Data.leg,
                    type: round2Data.type,
                    status: 'open'
                });
            }

        } catch (err) {
            console.error("Error starting intermediate round:", err);
            setError("Fout bij het starten van de tussenronde.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetIntermediateRound = async () => {
        setConfirmResetIntermediateOpen(false);
        setLoading(true);
        setError(null);

        try {
            // Verwijder het liveRankingIntermediateRound document
            const liveRankingIntermediateRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingIntermediateRound');
            const batch = writeBatch(db);
            batch.delete(liveRankingIntermediateRef);
            
            // Verwijder de tussenronde schedule documenten (dynamische IDs)
            // Gebruik de intermediateRounds uit de huidige schedule
            intermediateRounds.forEach(([roundId]) => {
                const roundRef = doc(db, 'auctions', auctionId, 'schedule', roundId);
                batch.delete(roundRef);
            });
            
            // Backward compatibility: verwijder ook oude naming indien aanwezig
            const intermediateRound1Ref = doc(db, 'auctions', auctionId, 'schedule', 'intermediateRound1');
            const intermediateRound2Ref = doc(db, 'auctions', auctionId, 'schedule', 'intermediateRound2');
            batch.delete(intermediateRound1Ref);
            batch.delete(intermediateRound2Ref);
            
            await batch.commit();

        } catch (err) {
            console.error("Error resetting intermediate round:", err);
            setError("Fout bij het resetten van de tussenronde.");
        } finally {
            setLoading(false);
        }
    };

    // --- Finaleronde handlers ---
    const handleStartFinalRound = async () => {
        setConfirmStartFinalRoundOpen(false);
        setLoading(true);
        setError(null);

        try {
            // Valideer dat er 2 achtste finale rondes zijn
            if (knockoutAchtsteRounds.length < 2) {
                setError("Geen achtste finale rondes gevonden. Voer eerst de knock-out loting uit.");
                setLoading(false);
                return;
            }

            const round1 = knockoutAchtsteRounds[0]; // [roundKey, roundData] - leg 1/2
            const round2 = knockoutAchtsteRounds[1]; // [roundKey, roundData] - leg 2/2

            // Valideer dat beide rondes gesloten zijn
            if (round1[1].status !== 'closed' || round2[1].status !== 'closed') {
                setError("Beide achtste finale rondes moeten gesloten zijn voordat de finaleronde gestart kan worden.");
                setLoading(false);
                return;
            }

            const matches = round1[1].matches || [];
            if (matches.length === 0) {
                setError("Geen wedstrijden gevonden in de achtste finale.");
                setLoading(false);
                return;
            }

            // Haal roundRankings direct uit Firestore op (niet afhankelijk van matchScores state)
            const [rr1Snap, rr2Snap] = await Promise.all([
                getDoc(doc(db, 'auctions', auctionId, 'roundRankings', round1[0])),
                getDoc(doc(db, 'auctions', auctionId, 'roundRankings', round2[0]))
            ]);

            if (!rr1Snap.exists() || !rr2Snap.exists()) {
                setError(`RoundRankings niet gevonden voor achtste finale (${round1[0]}: ${rr1Snap.exists()}, ${round2[0]}: ${rr2Snap.exists()}). Sluit eerst beide rondes.`);
                setLoading(false);
                return;
            }

            const rr1Results = rr1Snap.data().results || [];
            const rr2Results = rr2Snap.data().results || [];

            // Bepaal de winnaar per wedstrijd over 2 legs
            const winners = [];
            const unresolvedMatches = [];

            console.group('🏆 Start Finaleronde - Winnaarsberekening');

            for (let matchIndex = 0; matchIndex < matches.length; matchIndex++) {
                const match = matches[matchIndex];
                const homeUid = participantNameToUidMap.get(match.home);
                const awayUid = participantNameToUidMap.get(match.away);

                if (!homeUid || !awayUid) {
                    setError(`Deelnemer niet gevonden: ${!homeUid ? match.home : match.away}`);
                    setLoading(false);
                    console.groupEnd();
                    return;
                }

                // Haal scores uit roundRankings
                const home1 = rr1Results.find(r => r.uid === homeUid);
                const away1 = rr1Results.find(r => r.uid === awayUid);
                const home2 = rr2Results.find(r => r.uid === homeUid);
                const away2 = rr2Results.find(r => r.uid === awayUid);

                const homeScore1 = (home1?.playerPoints || 0) + (home1?.tacticPoints || 0) + (home1?.tacticCounter || 0);
                const awayScore1 = (away1?.playerPoints || 0) + (away1?.tacticPoints || 0) + (away1?.tacticCounter || 0);
                const homeScore2 = (home2?.playerPoints || 0) + (home2?.tacticPoints || 0) + (home2?.tacticCounter || 0);
                const awayScore2 = (away2?.playerPoints || 0) + (away2?.tacticPoints || 0) + (away2?.tacticCounter || 0);

                const totalHome = homeScore1 + homeScore2;
                const totalAway = awayScore1 + awayScore2;

                let winnerName = null;
                if (totalHome > totalAway) {
                    winnerName = match.home;
                } else if (totalAway > totalHome) {
                    winnerName = match.away;
                } else {
                    // Tiebreaker: wie meer scoorde in ronde 2
                    if (homeScore2 > awayScore2) {
                        winnerName = match.home;
                    } else if (awayScore2 > homeScore2) {
                        winnerName = match.away;
                    } else {
                        unresolvedMatches.push(`${match.home} vs ${match.away}`);
                    }
                }

                console.log(`Match ${matchIndex}: ${match.home} vs ${match.away}`,
                    `| Leg1: ${homeScore1}-${awayScore1}`,
                    `| Leg2: ${homeScore2}-${awayScore2}`,
                    `| Totaal: ${totalHome}-${totalAway}`,
                    `| Winnaar: ${winnerName || 'GELIJK'}`);

                if (winnerName) {
                    winners.push(winnerName);
                }
            }

            console.log('Winnaars:', winners);
            console.groupEnd();

            if (unresolvedMatches.length > 0) {
                setError(`De volgende wedstrijden zijn volledig gelijk en hebben geen winnaar: ${unresolvedMatches.join(', ')}. Controleer de scores.`);
                setLoading(false);
                return;
            }

            if (winners.length !== 8) {
                setError(`Verwacht 8 winnaars maar ${winners.length} gevonden. Controleer de achtste finale wedstrijden.`);
                setLoading(false);
                return;
            }

            // Map winnaar-namen naar participant objecten
            const finalParticipants = winners.map(name => {
                const uid = participantNameToUidMap.get(name);
                return { uid, name };
            });

            const missingParticipants = finalParticipants.filter(p => !p.uid);
            if (missingParticipants.length > 0) {
                setError(`Kon geen deelnemer-ID vinden voor: ${missingParticipants.map(p => p.name).join(', ')}`);
                setLoading(false);
                return;
            }

            // Maak een liveRankingFinaleronde met de 8 winnaars
            const finalRanking = finalParticipants.map(participant => ({
                uid: participant.uid,
                name: participant.name,
                matchPoints: 0,
                playerPoints: 0,
                tacticPoints: 0,
                tacticCounter: 0,
                wins: 0,
                draws: 0,
                losses: 0
            }));

            const liveRankingFinalRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingFinaleronde');
            await setDoc(liveRankingFinalRef, {
                ranking: finalRanking,
                createdAt: serverTimestamp(),
                lastUpdated: serverTimestamp()
            });

            // Sla ook finalRoundParticipants op voor snelle verwijzing
            const finalRoundParticipantsRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRoundParticipants');
            await setDoc(finalRoundParticipantsRef, {
                participantUids: finalParticipants.map(p => p.uid),
                participants: finalParticipants,
                createdAt: serverTimestamp()
            });

        } catch (err) {
            console.error("Error starting final round:", err);
            setError("Fout bij het starten van de finaleronde.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetFinalRound = async () => {
        setConfirmResetFinalRoundOpen(false);
        setLoading(true);
        setError(null);

        try {
            const batch = writeBatch(db);

            // Verwijder liveRankingFinaleronde
            const liveRankingFinalRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingFinaleronde');
            batch.delete(liveRankingFinalRef);

            // Verwijder finalRoundParticipants
            const finalRoundParticipantsRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRoundParticipants');
            batch.delete(finalRoundParticipantsRef);

            // Verwijder finale ronde schedule documenten
            Object.keys(finalRounds).forEach(roundId => {
                const roundRef = doc(db, 'auctions', auctionId, 'schedule', roundId);
                batch.delete(roundRef);
            });

            await batch.commit();

        } catch (err) {
            console.error("Error resetting final round:", err);
            setError("Fout bij het resetten van de finaleronde.");
        } finally {
            setLoading(false);
        }
    };

    // --- Knock-out handlers ---
    const handleKnockoutLoting = async () => {
        setConfirmKnockoutLotingOpen(false);
        setLoading(true);
        setError(null);

        try {
            // Valideer spelfase
            const allowedPhases = ['Draft', 'Knock-out'];
            if (!allowedPhases.includes(auction?.spelfase)) {
                setError("Knock-out loting is alleen mogelijk in de spelfases 'Draft' en 'Knock-out'.");
                setLoading(false);
                return;
            }

            // Haal finalRankingPoule op
            const finalRankingPouleRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRankingPoule');
            const finalRankingPouleSnap = await getDoc(finalRankingPouleRef);
            if (!finalRankingPouleSnap.exists()) {
                setError("Geen eindstand poulefase gevonden. Sluit eerst de poulefase af.");
                setLoading(false);
                return;
            }

            const finalRankingPouleData = finalRankingPouleSnap.data();
            const pouleRanking = [...(finalRankingPouleData.ranking || [])].sort((a, b) => {
                if (b.matchPoints !== a.matchPoints) return b.matchPoints - a.matchPoints;
                const aScore = (a.playerPoints || 0) + (a.tacticPoints || 0) + (a.tacticCounter || 0);
                const bScore = (b.playerPoints || 0) + (b.tacticPoints || 0) + (b.tacticCounter || 0);
                return bScore - aScore;
            });

            // Haal liveRankingIntermediateRound op voor #1 tussenronde
            const liveRankingIntermediateRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingIntermediateRound');
            const liveRankingIntermediateSnap = await getDoc(liveRankingIntermediateRef);

            // Top 15 poulefase
            const top15 = pouleRanking.slice(0, 15);
            if (top15.length < 15) {
                setError(`Onvoldoende deelnemers in de poulefase (${top15.length}/15). Er zijn minimaal 15 nodig.`);
                setLoading(false);
                return;
            }

            // #1 tussenronde
            let intermediateWinner = null;
            if (liveRankingIntermediateSnap.exists()) {
                const intermediateRanking = [...(liveRankingIntermediateSnap.data().ranking || [])].sort((a, b) => {
                    return (b.playerPoints || 0) - (a.playerPoints || 0);
                });
                if (intermediateRanking.length > 0) {
                    intermediateWinner = intermediateRanking[0];
                }
            }

            if (!intermediateWinner) {
                setError("Geen winnaar van de tussenronde gevonden. Start en sluit eerst de tussenronde af.");
                setLoading(false);
                return;
            }

            // Combineer 16 deelnemers: seeds #1-8 poulefase, opponents #9-15 poulefase + #1 tussenronde
            const seeds = top15.slice(0, 8); // #1-8 poulefase
            const pouleOpponents = top15.slice(8, 15); // #9-15 poulefase
            const opponents = [...pouleOpponents, { uid: intermediateWinner.uid, name: intermediateWinner.name }];

            // Shuffle opponents willekeurig
            for (let i = opponents.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [opponents[i], opponents[j]] = [opponents[j], opponents[i]];
            }

            // Maak 8 wedstrijdparen
            const matches = seeds.map((seed, i) => ({
                home: seed.name,
                away: opponents[i].name
            }));

            // Haal de Round of 16 stage info uit de geïmporteerde schedule
            const seasonId = auction?.season;
            if (!seasonId) {
                setError("Geen seizoen gevonden voor deze veiling.");
                setLoading(false);
                return;
            }

            const scheduleRoundsRef = collection(db, 'schedule', String(seasonId), 'rounds');
            const scheduleRoundsSnapshot = await getDocs(scheduleRoundsRef);

            let roundOf16Rounds = [];
            scheduleRoundsSnapshot.docs.forEach(docSnap => {
                const data = docSnap.data();
                if (data.stageName === '8th Finals' && data.type === 'knockout') {
                    roundOf16Rounds.push({
                        docId: docSnap.id,
                        ...data
                    });
                }
            });

            // Sorteer op leg (1/2 voor 2/2)
            roundOf16Rounds.sort((a, b) => (a.leg || '').localeCompare(b.leg || ''));

            if (roundOf16Rounds.length < 2) {
                setError("Geen 8th Finals data gevonden in de Sportmonks schedule. Importeer eerst het wedstrijdschema.");
                setLoading(false);
                return;
            }

            const round1Data = roundOf16Rounds[0];
            const round2Data = roundOf16Rounds[1];

            // Verwijder eventuele bestaande achtste finale schedule docs
            const batch = writeBatch(db);
            knockoutAchtsteRounds.forEach(([roundId]) => {
                batch.delete(doc(db, 'auctions', auctionId, 'schedule', roundId));
            });

            // Maak 2 nieuwe schedule docs (zelfde wedstrijden, verschillende legs)
            const round1Ref = doc(db, 'auctions', auctionId, 'schedule', round1Data.docId);
            batch.set(round1Ref, {
                matches: matches,
                status: 'open',
                roundId: round1Data.docId,
                stageId: round1Data.stageId,
                stageName: '8th Finals',
                leg: round1Data.leg || '1/2',
                type: 'knockout'
            });

            const round2Ref = doc(db, 'auctions', auctionId, 'schedule', round2Data.docId);
            batch.set(round2Ref, {
                matches: matches,
                status: 'open',
                roundId: round2Data.docId,
                stageId: round2Data.stageId,
                stageName: '8th Finals',
                leg: round2Data.leg || '2/2',
                type: 'knockout'
            });

            await batch.commit();
        } catch (err) {
            console.error("Error during knockout loting:", err);
            setError("Er is een fout opgetreden bij het genereren van de knock-out loting.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetKnockoutLoting = async () => {
        setConfirmResetKnockoutLotingOpen(false);
        setLoading(true);
        setError(null);

        try {
            const batch = writeBatch(db);

            // Verwijder de achtste finale schedule docs
            knockoutAchtsteRounds.forEach(([roundId]) => {
                batch.delete(doc(db, 'auctions', auctionId, 'schedule', roundId));
                // Verwijder ook de bijbehorende roundRankings
                batch.delete(doc(db, 'auctions', auctionId, 'roundRankings', roundId));
            });

            await batch.commit();
        } catch (err) {
            console.error("Error resetting knockout loting:", err);
            setError("Fout bij het verwijderen van de knock-out loting.");
        } finally {
            setLoading(false);
        }
    };

    // --- Draft handlers ---
    const draftParticipantsWithNames = useMemo(() => {
        if (!draftConfig?.draftParticipants) return [];
        return draftConfig.draftParticipants.map(uid => {
            const participant = participants.find(p => (p.uid || p.id) === uid);
            return { uid, name: participant?.name || 'Onbekend' };
        });
    }, [draftConfig, participants]);

    const draftEliminatedWithNames = useMemo(() => {
        if (!draftConfig?.eliminatedParticipants) return [];
        return draftConfig.eliminatedParticipants.map(uid => {
            const participant = participants.find(p => (p.uid || p.id) === uid);
            return { uid, name: participant?.name || 'Onbekend' };
        });
    }, [draftConfig, participants]);

    // Draft transfers (picks die al zijn gedaan)
    const draftTransfers = useMemo(() => {
        return transfers.filter(t => t.type === 'Draft').sort((a, b) => (a.draftPickIndex || 0) - (b.draftPickIndex || 0));
    }, [transfers]);

    const handleDraftLoting = async () => {
        setConfirmDraftLotingOpen(false);
        setLoading(true);
        setError(null);

        try {
            if (!draftConfig) {
                setError('Geen draft configuratie gevonden. Zet de spelfase eerst op Draft.');
                setLoading(false);
                return;
            }

            const draftParticipantUids = draftConfig.draftParticipants || [];
            if (draftParticipantUids.length === 0) {
                setError('Geen deelnemers gevonden voor de draft.');
                setLoading(false);
                return;
            }

            // Shuffle voor random volgorde
            const shuffled = [...draftParticipantUids].sort(() => Math.random() - 0.5);

            // Snake draft: heen + terug
            const draftOrder = [...shuffled, ...shuffled.slice().reverse()];

            const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
            await setDoc(draftConfigRef, {
                ...draftConfig,
                draftOrder: draftOrder,
                currentPickIndex: 0,
                status: 'lottery_done',
                updatedAt: serverTimestamp()
            });

        } catch (err) {
            console.error('Error during draft loting:', err);
            setError('Fout bij het uitvoeren van de draft loting.');
        } finally {
            setLoading(false);
        }
    };

    const handleDraftStart = async () => {
        setConfirmDraftStartOpen(false);
        setLoading(true);
        setError(null);

        try {
            const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
            await setDoc(draftConfigRef, {
                ...draftConfig,
                status: 'active',
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error('Error starting draft:', err);
            setError('Fout bij het starten van de draft.');
        } finally {
            setLoading(false);
        }
    };

    const handleDraftResetLoting = async () => {
        setConfirmDraftResetLotingOpen(false);
        setLoading(true);
        setError(null);

        try {
            // Verwijder alle draft transfers
            const batch = writeBatch(db);
            const draftTransfersQuery = query(
                collection(db, 'auctions', auctionId, 'auctionTransfers'),
                where('type', '==', 'Draft')
            );
            const draftTransfersSnap = await getDocs(draftTransfersQuery);
            draftTransfersSnap.docs.forEach(transferDoc => {
                batch.delete(transferDoc.ref);
            });

            // Reset draft config
            const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
            batch.update(draftConfigRef, {
                draftOrder: [],
                currentPickIndex: 0,
                status: 'not_started',
                updatedAt: serverTimestamp()
            });

            await batch.commit();
        } catch (err) {
            console.error('Error resetting draft loting:', err);
            setError('Fout bij het resetten van de draft loting.');
        } finally {
            setLoading(false);
        }
    };

    const handleSetDraftTurn = async (pickIndex) => {
        setLoading(true);
        setError(null);
        try {
            const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
            await setDoc(draftConfigRef, {
                ...draftConfig,
                currentPickIndex: pickIndex,
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error('Error setting draft turn:', err);
            setError('Fout bij het wijzigen van de beurt.');
        } finally {
            setLoading(false);
        }
    };

    const handleDraftClose = async () => {
        setConfirmDraftCloseOpen(false);
        setLoading(true);
        setError(null);

        try {
            const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
            await setDoc(draftConfigRef, {
                ...draftConfig,
                status: 'completed',
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error('Error closing draft:', err);
            setError('Fout bij het sluiten van de draft.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Herstart de volledige draft: draai alle draft-transfers terug,
     * verwijder offerings, en reset de config naar not_started.
     */
    const handleDraftRestart = async () => {
        setConfirmDraftRestartOpen(false);
        setLoading(true);
        setError(null);

        try {
            const batch = writeBatch(db);

            // 1. Haal alle Draft transfers op
            const draftTransfersQuery = query(
                collection(db, 'auctions', auctionId, 'auctionTransfers'),
                where('type', '==', 'Draft')
            );
            const draftTransfersSnap = await getDocs(draftTransfersQuery);

            // 2. Bouw een map van teamwijzigingen om terug te draaien
            const teamChanges = new Map(); // uid -> { add: Set, remove: Set }

            draftTransfersSnap.docs.forEach(transferDoc => {
                const transfer = transferDoc.data();
                const toUid = transfer.to;
                const fromUid = transfer.from;
                const playerId = String(transfer.playerId);

                // Verwijder speler uit ontvanger's team
                if (!teamChanges.has(toUid)) teamChanges.set(toUid, { add: new Set(), remove: new Set() });
                teamChanges.get(toUid).remove.add(playerId);

                // Voeg speler terug toe aan originele eigenaar (als het geen vrije markt was)
                if (fromUid && fromUid !== 'vrije-markt') {
                    if (!teamChanges.has(fromUid)) teamChanges.set(fromUid, { add: new Set(), remove: new Set() });
                    teamChanges.get(fromUid).add.add(playerId);
                }

                // Verwijder transfer record
                batch.delete(transferDoc.ref);
            });

            // 3. Pas team documenten aan
            for (const [uid, changes] of teamChanges) {
                const teamRef = doc(db, 'auctions', auctionId, 'teams', uid);
                const teamDoc = await getDoc(teamRef);
                if (teamDoc.exists()) {
                    let players = (teamDoc.data().players || []).map(String);
                    // Verwijder gedrafte spelers
                    players = players.filter(id => !changes.remove.has(id));
                    // Voeg originele spelers terug
                    changes.add.forEach(id => {
                        if (!players.includes(id)) players.push(id);
                    });
                    batch.update(teamRef, { players });
                }
            }

            // 4. Reset draft config (behoud draftOrder en offerings)
            const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
            batch.update(draftConfigRef, {
                currentPickIndex: 0,
                status: 'lottery_done',
                updatedAt: serverTimestamp()
            });

            await batch.commit();
        } catch (err) {
            console.error('Error restarting draft:', err);
            setError('Fout bij het herstarten van de draft.');
        } finally {
            setLoading(false);
        }
    };

    const handleStartBlindPoolRound = async () => {
        const activeRound = blindPoolRounds.find(r => r.status === 'open');
        if (activeRound) {
            alert('Er is al een actieve blinde pool ronde.');
            return;
        }

        setLoading(true);
        try {
            const blindPoolRoundsCol = collection(db, 'auctions', auctionId, 'blindPoolRounds');

            await addDoc(blindPoolRoundsCol, {
                roundNumber: blindPoolRounds.length + 1,
                status: 'open',
                createdAt: serverTimestamp()
            });

        } catch (err) {
            console.error("Error starting blind pool round:", err);
            setError("Kon geen nieuwe blinde pool ronde starten.");
        } finally {
            setLoading(false);
        }
    };

    // Haalt de biedingen van een ronde vers op vanaf de server, zodat de berekening
    // nooit op een verouderde momentopname (popup-cache of trage listener) draait.
    const fetchRoundBids = async (roundId) => {
        const bidsSnapshot = await getDocs(
            collection(db, 'auctions', auctionId, 'blindPoolRounds', roundId, 'bids')
        );
        return bidsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    };

    // Bepaalt op basis van de biedingen wie welke speler hoort te krijgen.
    // Met options.excludeRoundId worden de bestaande toekenningen van die ronde
    // niet meegeteld in de teamgrootte, zodat een herberekening van dezelfde ronde
    // klopt (de plekken die in die ronde zijn toegekend komen dan weer vrij).
    const calculateProposedAssignments = (roundId, bids, options = {}) => {
        const { excludeRoundId = null } = options;
        const participantTeamSizes = new Map();
        participants.forEach(p => participantTeamSizes.set(p.uid, 0));

        transfers.forEach(t => {
            if (excludeRoundId && t.type === 'blindPoolAssignment' && t.roundId === excludeRoundId) {
                return;
            }
            if (participantTeamSizes.has(t.to)) {
                participantTeamSizes.set(t.to, participantTeamSizes.get(t.to) + 1);
            }
        });

        const assignments = [];
        const assignedPlayers = new Set();

        const bidsByPlayer = bids.reduce((acc, bid) => {
            if (!acc[bid.playerId]) {
                acc[bid.playerId] = [];
            }
            acc[bid.playerId].push(bid);
            return acc;
        }, {});

        for (const playerId in bidsByPlayer) {
            const playerBids = bidsByPlayer[playerId];
            playerBids.sort((a, b) => {
                if (b.amount !== a.amount) {
                    return b.amount - a.amount;
                }
                // Bij een gelijk bod wint de vroegste bieder.
                const aTime = a.timestamp?.toMillis?.() ?? 0;
                const bTime = b.timestamp?.toMillis?.() ?? 0;
                return aTime - bTime;
            });

            for (const bid of playerBids) {
                const participantCurrentTeamSize = participantTeamSizes.get(bid.participantId) || 0;
                const maxTeamSize = 12;

                if (!assignedPlayers.has(bid.playerId) && participantCurrentTeamSize < maxTeamSize) {
                    assignments.push({
                        roundId: roundId,
                        participantId: bid.participantId,
                        participantName: bid.participantName,
                        playerId: bid.playerId,
                        playerName: bid.playerName,
                        amount: bid.amount,
                        priority: bid.priority
                    });
                    assignedPlayers.add(bid.playerId);
                    participantTeamSizes.set(bid.participantId, participantCurrentTeamSize + 1);
                    break;
                }
            }
        }
        return assignments;
    };

    const handleAssignPlayers = (roundId, bids) => {
        setCurrentRoundForAssignment(roundId);
        const assignments = calculateProposedAssignments(roundId, bids);
        setProposedAssignments(assignments);
        setOpenAssignmentModal(true);
    };

    const handleConfirmAssignments = async () => {
        setOpenAssignmentModal(false);
        if (!currentRoundForAssignment) return;
        setLoading(true);
        try {
            // Herbereken op verse biedingen vanaf de server i.p.v. de bevroren
            // preview. Zo tellen biedingen die na het openen van de popup zijn
            // binnengekomen alsnog mee.
            const freshBids = await fetchRoundBids(currentRoundForAssignment);
            const assignments = calculateProposedAssignments(currentRoundForAssignment, freshBids);

            const batch = writeBatch(db);
            const auctionTransfersRef = collection(db, 'auctions', auctionId, 'auctionTransfers');

            for (const assignment of assignments) {
                const newTransferRef = doc(auctionTransfersRef);
                batch.set(newTransferRef, {
                    roundId: assignment.roundId,
                    playerId: assignment.playerId,
                    playerName: assignment.playerName,
                    from: 'blindPool',
                    to: assignment.participantId,
                    amount: assignment.amount,
                    timestamp: serverTimestamp(),
                    type: 'blindPoolAssignment'
                });

                const participantRef = doc(db, 'auctions', auctionId, 'participants', assignment.participantId);
                batch.update(participantRef, {
                    budget: increment(-assignment.amount),
                    transfersLeft: increment(-1)
                });
            }

            const roundRef = doc(db, 'auctions', auctionId, 'blindPoolRounds', currentRoundForAssignment);
            batch.update(roundRef, { status: 'closed', closedAt: serverTimestamp() });

            await batch.commit();
            alert(`Spelers succesvol toegekend en ronde gesloten! (${assignments.length} toekenning(en))`);
            setProposedAssignments([]);
            setCurrentRoundForAssignment(null);
        } catch (err) {
            console.error("Error confirming assignments:", err);
            setError("Fout bij het toekennen van spelers.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseAssignmentModal = () => {
        setOpenAssignmentModal(false);
        setProposedAssignments([]);
        setCurrentRoundForAssignment(null);
    };

    // Herbepalen: controleert een reeds gesloten ronde opnieuw op basis van de
    // actuele biedingen en bepaalt welke bestaande toekenningen fout zijn.
    // Bouwt een plan (te verwijderen + toe te voegen transfers) en toont dat ter
    // bevestiging; het daadwerkelijk toepassen gebeurt in handleConfirmRecalculate.
    const handleRecalculate = async (roundId) => {
        setLoading(true);
        try {
            const freshBids = await fetchRoundBids(roundId);
            const correctAssignments = calculateProposedAssignments(roundId, freshBids, { excludeRoundId: roundId });

            // Bestaande toekenningen van deze ronde ophalen.
            const existingSnapshot = await getDocs(query(
                collection(db, 'auctions', auctionId, 'auctionTransfers'),
                where('roundId', '==', roundId),
                where('type', '==', 'blindPoolAssignment')
            ));
            const existingTransfers = existingSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            const correctByPlayer = new Map();
            correctAssignments.forEach(a => correctByPlayer.set(String(a.playerId), a));

            const toDelete = [];
            const unchanged = [];

            existingTransfers.forEach(t => {
                const correct = correctByPlayer.get(String(t.playerId));
                const matches = correct && correct.participantId === t.to && correct.amount === t.amount;
                if (matches) {
                    unchanged.push({ ...correct });
                    correctByPlayer.delete(String(t.playerId));
                } else {
                    toDelete.push(t);
                }
            });

            // Wat overblijft in correctByPlayer moet nieuw aangemaakt worden.
            const toCreate = Array.from(correctByPlayer.values());

            setRoundToRecalculate(roundId);
            setRecalculatePlan({ toDelete, toCreate, unchanged });
            setOpenRecalculateModal(true);
        } catch (err) {
            console.error("Error recalculating assignments:", err);
            setError("Fout bij het herbepalen van de toekenningen.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseRecalculateModal = () => {
        setOpenRecalculateModal(false);
        setRecalculatePlan(null);
        setRoundToRecalculate(null);
    };

    const handleConfirmRecalculate = async () => {
        setOpenRecalculateModal(false);
        if (!recalculatePlan || !roundToRecalculate) return;
        setLoading(true);
        try {
            const { toDelete, toCreate } = recalculatePlan;
            const batch = writeBatch(db);
            const auctionTransfersRef = collection(db, 'auctions', auctionId, 'auctionTransfers');

            // Foute transfers verwijderen en budget + transfer terugboeken.
            for (const t of toDelete) {
                const transferRef = doc(db, 'auctions', auctionId, 'auctionTransfers', t.id);
                batch.delete(transferRef);
                const participantRef = doc(db, 'auctions', auctionId, 'participants', t.to);
                batch.update(participantRef, {
                    budget: increment(t.amount),
                    transfersLeft: increment(1)
                });
            }

            // Nieuwe, correcte transfers aanmaken en budget + transfer afboeken.
            for (const assignment of toCreate) {
                const newTransferRef = doc(auctionTransfersRef);
                batch.set(newTransferRef, {
                    roundId: assignment.roundId,
                    playerId: assignment.playerId,
                    playerName: assignment.playerName,
                    from: 'blindPool',
                    to: assignment.participantId,
                    amount: assignment.amount,
                    timestamp: serverTimestamp(),
                    type: 'blindPoolAssignment'
                });
                const participantRef = doc(db, 'auctions', auctionId, 'participants', assignment.participantId);
                batch.update(participantRef, {
                    budget: increment(-assignment.amount),
                    transfersLeft: increment(-1)
                });
            }

            await batch.commit();
            alert(`Herbepaling voltooid: ${toDelete.length} foute toekenning(en) verwijderd, ${toCreate.length} nieuwe toekenning(en) aangemaakt.`);
            setRecalculatePlan(null);
            setRoundToRecalculate(null);
        } catch (err) {
            console.error("Error applying recalculation:", err);
            setError("Fout bij het toepassen van de herbepaling.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenConfirmDeleteRound = (roundId) => {
        setRoundToDelete(roundId);
        setConfirmDeleteRoundOpen(true);
    };

    const handleCloseConfirmDeleteRound = () => {
        setRoundToDelete(null);
        setConfirmDeleteRoundOpen(false);
    };

    const handleConfirmDeleteRound = async () => {
        if (!roundToDelete) return;

        setLoading(true);
        try {
            const batch = writeBatch(db);

            const bidsQuery = query(collection(db, 'auctions', auctionId, 'blindPoolRounds', roundToDelete, 'bids'));
            const bidsSnapshot = await getDocs(bidsQuery);
            bidsSnapshot.forEach(doc => {
                batch.delete(doc.ref);
            });

            const transfersQuery = query(
                collection(db, 'auctions', auctionId, 'auctionTransfers'),
                where('roundId', '==', roundToDelete),
                where('type', '==', 'blindPoolAssignment')
            );
            const transfersSnapshot = await getDocs(transfersQuery);

            transfersSnapshot.forEach(transferDoc => {
                const transfer = transferDoc.data();
                const participantRef = doc(db, 'auctions', auctionId, 'participants', transfer.to);
                batch.update(participantRef, { budget: increment(transfer.amount) });
                batch.delete(transferDoc.ref);
            });

            const roundRef = doc(db, 'auctions', auctionId, 'blindPoolRounds', roundToDelete);
            batch.delete(roundRef);

            await batch.commit();

        } catch (err) {
            console.error("Error deleting blind pool round:", err);
            setError("Fout bij het verwijderen van de blinde pool ronde en bijbehorende data.");
        } finally {
            setLoading(false);
            handleCloseConfirmDeleteRound();
        }
    };

    const handleOpenConfirmDeleteBid = (bid, roundId) => {
        setBidToDelete({ ...bid, roundId });
        setConfirmDeleteBidOpen(true);
    };

    const handleCloseConfirmDeleteBid = () => {
        setBidToDelete(null);
        setConfirmDeleteBidOpen(false);
    };

    const handleConfirmDeleteBid = async () => {
        if (!bidToDelete) return;

        setLoading(true);
        try {
            const batch = writeBatch(db);

            const bidRef = doc(db, 'auctions', auctionId, 'blindPoolRounds', bidToDelete.roundId, 'bids', bidToDelete.id);
            batch.delete(bidRef);

            const transfersQuery = query(
                collection(db, 'auctions', auctionId, 'auctionTransfers'),
                where('roundId', '==', bidToDelete.roundId),
                where('playerId', '==', bidToDelete.playerId),
                where('type', '==', 'blindPoolAssignment'),
                where('to', '==', bidToDelete.participantId)
            );
            const transfersSnapshot = await getDocs(transfersQuery);

            let amountToRefund = 0;
            let transferDeleted = false;
            transfersSnapshot.forEach(transferDoc => {
                const transfer = transferDoc.data();
                amountToRefund = transfer.amount;
                batch.delete(transferDoc.ref);
                transferDeleted = true;
            });

            if (transferDeleted) {
                const participantRef = doc(db, 'auctions', auctionId, 'participants', bidToDelete.participantId);
                batch.update(participantRef, {
                    budget: increment(amountToRefund),
                    transfersLeft: increment(1)
                });
            }

            await batch.commit();
            alert('Bieding en transfer succesvol verwijderd.');

        } catch (err) {
            console.error("Error deleting blind pool bid:", err);
            setError("Fout bij het verwijderen van de bieding en bijbehorende transfer.");
        } finally {
            setLoading(false);
            handleCloseConfirmDeleteBid();
        }
    };

    // === Transferrondes handlers ===

    // Spelfases waarin transferrondes NIET zijn toegestaan
    const transferRoundForbiddenPhases = ['Voorbereiding', 'Veiling', 'Blinde Pool', 'Poulefase', 'Tussenronde', 'Draft'];
    const transferRoundAllowed = auction?.spelfase && !transferRoundForbiddenPhases.includes(auction.spelfase);

    // Bereken het totaal aantal toegestane transfers op basis van de spelfase
    const getTotalAllowedTransfers = (phase) => {
        const preAuction = ['Voorbereiding', 'Veiling', 'Blinde Pool'];
const preKnockout = ['Poulefase', 'Tussenronde', 'Draft'];
    const knockout = ['Knock-out', 'Finaleronde'];

        if (preAuction.includes(phase)) return 12;
        if (preKnockout.includes(phase)) return 15;
        if (knockout.includes(phase)) return 19;
        return 0;
    };

    // Bereken transfersLeft per deelnemer op basis van werkelijke transfers (zelfde logica als AdminOverview)
    const computedTransfersLeftMap = useMemo(() => {
        const totalAllowed = getTotalAllowedTransfers(auction?.spelfase);
        const map = new Map();

        participants.forEach(p => {
            const uid = p.id || p.uid;
            const sortedTransfers = [...transfers].sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0));

            const incomingTransfers = sortedTransfers.filter(t => t.to === uid);

            // Sluit coach-change en Draft transfers uit (initiële teamopbouw)
            const excludedTypes = ['coach-change', 'Draft'];
            const transfersUsed = incomingTransfers.filter(t => !excludedTypes.includes(t.type)).length;
            const nonClTransfersCount = sortedTransfers.filter(t => t.from === uid && REFUNDED_TRANSFER_TYPES.includes(t.type)).length;
            const correctTransfersLeft = totalAllowed - transfersUsed + nonClTransfersCount;

            map.set(uid, correctTransfersLeft);
        });

        return map;
    }, [participants, transfers, auction?.spelfase]);

    // Bereken of de toernooifase/wedstrijd selectie compleet is
    const trIsLeague = isLeagueStage(trStages, trSelectedStage);
    const trCanProceed = trSelectedStage && (trIsLeague ? trSelectedRound : trSelectedLeg);
    const trStorageId = trCanProceed ? getEffectiveStorageId(trStages, trSelectedStage, trSelectedRound, trSelectedLeg) : null;

    // Algoritme: bereken welke deelnemers geselecteerd worden voor de transferronde
    const calculateTransferRoundParticipants = () => {
        if (!trStorageId) return null;

        // Filter actieve deelnemers (niet geëlimineerd)
        const eliminatedUids = new Set(draftConfig?.eliminatedParticipants || []);
        let activeParticipants = participants.filter(p => !eliminatedUids.has(p.uid || p.id));

        // In de Finaleronde: alleen finalisten zijn actief
        if (auction?.spelfase === 'Finaleronde' && finalRoundParticipants?.participantUids?.length) {
            const finalistUids = new Set(finalRoundParticipants.participantUids);
            activeParticipants = activeParticipants.filter(p => finalistUids.has(p.uid || p.id));
        }

        if (activeParticipants.length === 0) return null;

        // Zoek de vorige gesloten transferronde met dezelfde storageId
        const previousRound = transferRounds.find(
            r => r.status === 'closed' && r.storageId === trStorageId
        );

        // Bepaal carry-over deelnemers: deelnemers die hun volledige allowance niet gebruikten
        // Tel alleen transfers die BINNEN de vorige ronde vallen (niet globaal)
        let carryOverIds = new Set();
        if (previousRound?.participants) {
            const prevStartSeconds = previousRound.createdAt?.seconds || 0;
            const prevEndSeconds = previousRound.closedAt?.seconds || null;
            const excludedTypes = ['coach-change', 'Draft'];

            // Filter transfers die binnen het tijdsvenster van de vorige ronde vallen
            const prevRoundTransfers = transfers.filter(t => {
                if (!t.timestamp?.seconds) return false;
                const ts = t.timestamp.seconds;
                if (ts < prevStartSeconds) return false;
                if (prevEndSeconds && ts > prevEndSeconds) return false;
                return true;
            });

            Object.entries(previousRound.participants).forEach(([participantId, data]) => {
                const liveData = activeParticipants.find(p => (p.id || p.uid) === participantId);
                if (!liveData) return;
                // Tel transfers in de vorige ronde voor deze deelnemer
                const incomingInRound = prevRoundTransfers.filter(
                    t => t.to === participantId && !excludedTypes.includes(t.type)
                ).length;
                const nonClInRound = prevRoundTransfers.filter(
                    t => t.from === participantId && REFUNDED_TRANSFER_TYPES.includes(t.type)
                ).length;
                const transfersDone = Math.max(0, incomingInRound - nonClInRound);
                if (transfersDone < data.allowance) {
                    carryOverIds.add(participantId);
                }
            });
        }

        // Sorteer alle actieve deelnemers op berekende transfersLeft (aflopend)
        const sorted = [...activeParticipants]
            .map(p => {
                const uid = p.id || p.uid;
                return { id: uid, name: p.name, transfersLeft: computedTransfersLeftMap.get(uid) ?? 0 };
            })
            .sort((a, b) => b.transfersLeft - a.transfersLeft);

        // Begin met carry-overs
        const selected = new Set(carryOverIds);

        // Bepaal het maximale transfersLeft van de carry-overs
        let carryOverMaxTransfers = 0;
        carryOverIds.forEach(id => {
            const p = sorted.find(s => s.id === id);
            if (p) carryOverMaxTransfers = Math.max(carryOverMaxTransfers, p.transfersLeft);
        });

        // Voeg de volgende groep toe: deelnemers met de hoogste transfersLeft die niet al carry-over zijn
        const nonCarryOver = sorted.filter(p => !selected.has(p.id) && p.transfersLeft > 0);
        if (nonCarryOver.length > 0) {
            // Het maximale transfersLeft van de niet-carry-over deelnemers
            const topTransfersLeft = nonCarryOver[0].transfersLeft;
            
            // Als er carry-overs zijn, nemen we de volgende groep erbij tot aan dat niveau
            // Als er geen carry-overs zijn, nemen we alleen de top groep
            if (carryOverIds.size > 0) {
                // Selecteer alle niet-carry-overs met hetzelfde topniveau
                nonCarryOver.forEach(p => {
                    if (p.transfersLeft === topTransfersLeft) {
                        selected.add(p.id);
                    }
                });
            } else {
                // Geen carry-overs: selecteer alleen de top groep
                nonCarryOver.forEach(p => {
                    if (p.transfersLeft === topTransfersLeft) {
                        selected.add(p.id);
                    }
                });
            }
        }

        // Bepaal de floor: het hoogste transfersLeft van NIET-geselecteerde deelnemers
        const notSelected = sorted.filter(p => !selected.has(p.id));
        const floor = notSelected.length > 0 ? notSelected[0].transfersLeft : 0;

        // Bereken allowance per geselecteerde deelnemer
        const result = {};
        selected.forEach(id => {
            const p = sorted.find(s => s.id === id);
            if (!p) return;
            const allowance = p.transfersLeft - floor;
            if (allowance > 0) {
                result[id] = {
                    name: p.name,
                    allowance,
                    transfersLeftAtStart: p.transfersLeft
                };
            }
        });

        return Object.keys(result).length > 0 ? result : null;
    };

    const handleStartTransferRound = async () => {
        const activeRound = transferRounds.find(r => r.status === 'open');
        if (activeRound) {
            alert('Er is al een actieve transferronde.');
            return;
        }

        const selectedParticipants = calculateTransferRoundParticipants();
        if (!selectedParticipants) {
            alert('Geen deelnemers beschikbaar voor een transferronde met deze selectie.');
            return;
        }

        setLoading(true);
        try {
            const batch = writeBatch(db);

            const transferRoundsCol = collection(db, 'auctions', auctionId, 'transferRounds');
            const newRoundRef = doc(transferRoundsCol);
            const matchName = getMatchDisplayName(trStages, trSelectedStage, trRounds, trSelectedRound, trSelectedLeg);

            batch.set(newRoundRef, {
                roundNumber: transferRounds.length + 1,
                status: 'open',
                storageId: trStorageId,
                stageName: getTranslatedStageName(trStages.find(s => s.id === trSelectedStage)?.name) || '',
                matchName: matchName,
                participants: selectedParticipants,
                createdAt: serverTimestamp()
            });

            // Zet transfermarkt op gesloten
            const auctionRef = doc(db, 'auctions', auctionId);
            batch.update(auctionRef, { transfermarketClosed: true });

            await batch.commit();
            setConfirmStartTransferRoundOpen(false);
        } catch (err) {
            console.error("Error starting transfer round:", err);
            setError("Kon geen nieuwe transferronde starten.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseTransferRound = async () => {
        if (!transferRoundToAction) return;

        setLoading(true);
        try {
            const roundRef = doc(db, 'auctions', auctionId, 'transferRounds', transferRoundToAction);
            await setDoc(roundRef, { status: 'closed', closedAt: serverTimestamp() }, { merge: true });
            setConfirmCloseTransferRoundOpen(false);
            setTransferRoundToAction(null);
        } catch (err) {
            console.error("Error closing transfer round:", err);
            setError("Fout bij het sluiten van de transferronde.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTransferRound = async () => {
        if (!transferRoundToAction) return;

        setLoading(true);
        try {
            const roundRef = doc(db, 'auctions', auctionId, 'transferRounds', transferRoundToAction);
            const batch = writeBatch(db);
            batch.delete(roundRef);
            await batch.commit();
            setConfirmDeleteTransferRoundOpen(false);
            setTransferRoundToAction(null);
        } catch (err) {
            console.error("Error deleting transfer round:", err);
            setError("Fout bij het verwijderen van de transferronde.");
        } finally {
            setLoading(false);
        }
    };

    // Preview van de selectie voor de start-dialog
    const transferRoundPreview = trCanProceed ? calculateTransferRoundParticipants() : null;

    // Verwijder een individuele transfer en corrigeer transfersLeft van de deelnemer
    const handleDeleteTransfer = async (transferId, participantUid) => {
        if (!transferId) return;

        setLoading(true);
        try {
            const batch = writeBatch(db);

            // Verwijder het transfer document
            const transferRef = doc(db, 'auctions', auctionId, 'auctionTransfers', transferId);
            batch.delete(transferRef);

            // Corrigeer transfersLeft van de deelnemer (+1)
            if (participantUid && participantUid !== 'vrije-markt') {
                const participantRef = doc(db, 'auctions', auctionId, 'participants', participantUid);
                batch.update(participantRef, { transfersLeft: increment(1) });
            }

            await batch.commit();
        } catch (err) {
            console.error("Error deleting transfer:", err);
            setError("Fout bij het verwijderen van de transfer.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Spel Beheren
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="spel beheren tabs" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                    <Tab label="Ranglijst" {...a11yProps(0)} />
                    <Tab label="Wedstrijden" {...a11yProps(1)} />
                    <Tab label="Blinde Pool" {...a11yProps(2)} />
                    <Tab label="Draft" {...a11yProps(3)} />
                    <Tab label="Transferrondes" {...a11yProps(4)} />
                </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" gutterBottom component="div" sx={{ mb: 0 }}>Ranglijst</Typography>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            id="round-select-admin"
                            value={selectedRound}
                            onChange={(e) => setSelectedRound(e.target.value)}
                        >
                            {!['Tussenronde', 'Finaleronde'].includes(auction?.spelfase) && (
                                <MenuItem value="total">Totaal</MenuItem>
                            )}
                            {liveRankingIntermediateRound && (
                                <MenuItem value="liveRankingIntermediateRound">Totaal tussenronde</MenuItem>
                            )}
                            {(liveRankingFinaleronde || auction?.spelfase === 'Finaleronde') && (
                                <MenuItem value="liveRankingFinaleronde">Totaal finaleronde</MenuItem>
                            )}
                            {liveRankingIntermediateRound && intermediateRounds.map(([roundId, roundData], index) => {
                                // Bepaal ronde nummer
                                let roundNumber;
                                if (roundData.leg) {
                                    roundNumber = roundData.leg.split('/')[0];
                                } else if (roundId.startsWith('intermediateRound')) {
                                    roundNumber = roundId.replace('intermediateRound', '');
                                } else {
                                    roundNumber = index + 1;
                                }
                                return (
                                    <MenuItem key={roundId} value={roundId}>
                                        Tussenronde {roundNumber}
                                    </MenuItem>
                                );
                            })}
                            {finalRounds && Object.entries(finalRounds).map(([roundId, roundData]) => {
                                const label = roundData.stageName === 'Final' 
                                    ? 'Final' 
                                    : `${roundData.stageName} ${roundData.leg || ''}`;
                                return (
                                    <MenuItem key={roundId} value={roundId}>
                                        {label}
                                    </MenuItem>
                                );
                            })}
                            {knockoutAchtsteRounds.map(([roundId, roundData]) => {
                                const legNumber = roundData.leg ? roundData.leg.split('/')[0] : '';
                                return (
                                    <MenuItem key={roundId} value={roundId}>
                                        Achtste finale {legNumber}
                                    </MenuItem>
                                );
                            })}
                            {closedRounds.map(round => (
                                <MenuItem key={round.id} value={round.id}>
                                    Speelronde {round.id.replace('round', '')}
                                </MenuItem>
                            ))}
                            {finalRankingPoule && (
                                <MenuItem value="finalRankingPoule">Eindstand Poulefase</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Box>
                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <RankingTable ranking={rankings} onRowClick={handleRankingRowClick} showChampion={selectedRound === 'liveRankingFinaleronde' && isFinaleProcessed} showMedals={selectedRound === 'finalRankingPoule'} medalCounts={pouleWinCounts} />
                )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                {/* Finaleronde sectie - zichtbaar als spelfase Finaleronde is OF als er een finaleronde ranking bestaat */}
                {(auction?.spelfase === 'Finaleronde' || liveRankingFinaleronde) && (
                    <>
                        <Typography variant="h5" gutterBottom>Finaleronde</Typography>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 2 },
                            mb: 3,
                            alignItems: { xs: 'stretch', sm: 'center' }
                        }}>
                            {!liveRankingFinaleronde ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setConfirmStartFinalRoundOpen(true)}
                                    disabled={loading || !liveRankingIntermediateRound}
                                    sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                                >
                                    Start finaleronde
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setConfirmResetFinalRoundOpen(true)}
                                    disabled={loading}
                                    sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                                >
                                    Reset finaleronde
                                </Button>
                            )}
                        </Box>
                        {!liveRankingIntermediateRound && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                De tussenronde moet eerst worden afgesloten voordat de finaleronde kan worden gestart.
                            </Alert>
                        )}

                        {/* Finaleronde rondes - alleen zichtbaar als finaleronde is gestart */}
                        {liveRankingFinaleronde && Object.entries(finalRounds).length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                {Object.entries(finalRounds).map(([roundId, roundData], index) => {
                                    return (
                                        <Accordion key={roundId} defaultExpanded={index === 0} sx={{ mb: 2, backgroundImage: 'none' }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls={`final-panel${index}-content`}
                                                id={`final-panel${index}-header`}
                                                component="div"
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    '& .MuiAccordionSummary-content': {
                                                        margin: 0,
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        width: '100%'
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="h6">
                                                        {roundData.stageName} {roundData.leg ? `(${roundData.leg})` : ''}
                                                    </Typography>
                                                    <Tooltip title={
                                                        roundData.status === 'open' ? 'Ronde starten' :
                                                            roundData.status === 'running' ? 'Ronde terugzetten naar open' :
                                                                'Ronde is gesloten'
                                                    }>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (roundData.status === 'open') {
                                                                        openConfirmationDialog('start', roundId, roundData);
                                                                    } else if (roundData.status === 'running') {
                                                                        openConfirmationDialog('revert_to_open', roundId, roundData);
                                                                    }
                                                                }}
                                                                disabled={loading || !['open', 'running'].includes(roundData.status)}
                                                                color={roundData.status === 'running' ? 'success' : 'default'}
                                                            >
                                                                <SportsSoccerIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    {roundData.status === 'running' && (
                                                        <Tooltip title="Ronde sluiten">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openConfirmationDialog('close', roundId, roundData);
                                                                }}
                                                                disabled={loading}
                                                                color="primary"
                                                            >
                                                                <LockOpenIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {roundData.status === 'closed' && (
                                                        <Tooltip title="Ronde heropenen">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openConfirmationDialog('reopen', roundId, roundData);
                                                                }}
                                                                disabled={loading}
                                                                color="primary"
                                                            >
                                                                <LockIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Status: {roundData.status === 'open' ? 'Open - deelnemers kunnen aanvoerder kiezen' : 
                                                             roundData.status === 'running' ? 'Lopend - aanvoerder keuze afgesloten' : 
                                                             'Gesloten - punten berekend'}
                                                </Typography>
                                                {finalRoundParticipants?.participants && finalRoundParticipants.participants.length > 0 && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            Aanvoerder-keuzes ({finalRoundParticipants.participants.filter(p => {
                                                                const selectionId = `${roundId}-captain`;
                                                                return !!matchSelections[p.uid]?.[selectionId]?.captain;
                                                            }).length}/{finalRoundParticipants.participants.length})
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            {finalRoundParticipants.participants.map((participant) => {
                                                                const selectionId = `${roundId}-captain`;
                                                                const participantSelection = matchSelections[participant.uid]?.[selectionId] || {};
                                                                const captainChosen = !!participantSelection.captain;
                                                                const captainName = participantSelection.captain 
                                                                    ? (playerIdToNameMap.get(String(participantSelection.captain)) || 'Onbekend')
                                                                    : 'Niet gekozen';
                                                                return (
                                                                    <Box
                                                                        key={participant.uid}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                            py: 0.5,
                                                                            px: 1,
                                                                            borderRadius: 1,
                                                                            bgcolor: 'action.hover'
                                                                        }}
                                                                    >
                                                                        <Typography variant="body2">
                                                                            {participant.name}
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <StarIcon 
                                                                                fontSize="small" 
                                                                                sx={{ color: captainChosen ? 'success.main' : 'error.main' }} 
                                                                            />
                                                                            <Typography 
                                                                                variant="body2" 
                                                                                sx={{ 
                                                                                    color: captainChosen ? 'text.primary' : 'text.secondary',
                                                                                    fontStyle: captainChosen ? 'normal' : 'italic'
                                                                                }}
                                                                            >
                                                                                {captainName}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Box>
                        )}
                    </>
                )}

                {/* Knock-out sectie - zichtbaar als spelfase Draft/Knock-out is OF als er achtste finale rondes bestaan */}
                {(['Draft', 'Knock-out'].includes(auction?.spelfase) || knockoutAchtsteRounds.length > 0) && (
                    <>
                        <Typography variant="h5" gutterBottom>Knock-out — Achtste finales</Typography>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 2 },
                            mb: 3,
                            alignItems: { xs: 'stretch', sm: 'center' }
                        }}>
                            {knockoutAchtsteRounds.length === 0 ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setConfirmKnockoutLotingOpen(true)}
                                    disabled={loading || !finalRankingPoule || !liveRankingIntermediateRound || !['Draft', 'Knock-out'].includes(auction?.spelfase)}
                                    sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                                >
                                    Loting achtste finales
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => setConfirmKnockoutLotingOpen(true)}
                                        disabled={loading || !['Draft', 'Knock-out'].includes(auction?.spelfase)}
                                        sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                                    >
                                        Loting opnieuw
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setConfirmResetKnockoutLotingOpen(true)}
                                        disabled={loading}
                                        sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                                    >
                                        Verwijder loting
                                    </Button>
                                </>
                            )}
                        </Box>
                        {!finalRankingPoule && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                De poulefase moet eerst worden afgesloten voordat de knock-out loting kan worden uitgevoerd.
                            </Alert>
                        )}
                        {finalRankingPoule && !liveRankingIntermediateRound && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                De tussenronde moet eerst worden gestart en afgerond voordat de knock-out loting kan worden uitgevoerd.
                            </Alert>
                        )}
                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {/* Achtste finale speelrondes */}
                        {knockoutAchtsteRounds.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                {knockoutAchtsteRounds.map(([roundId, roundData], index) => {
                                    const legNumber = roundData.leg ? roundData.leg.split('/')[0] : index + 1;
                                    return (
                                        <Accordion key={roundId} defaultExpanded={index === 0} sx={{ mb: 2, backgroundImage: 'none' }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls={`knockout-panel${index}-content`}
                                                id={`knockout-panel${index}-header`}
                                                component="div"
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    '& .MuiAccordionSummary-content': {
                                                        margin: 0,
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        width: '100%'
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="h6">
                                                        Speelronde {legNumber}
                                                    </Typography>

                                                    {/* Voetbal icoon voor open/running toggle */}
                                                    <Tooltip title={
                                                        roundData.status === 'open' ? 'Ronde starten' :
                                                            roundData.status === 'running' ? 'Ronde terugzetten naar open' :
                                                                'Ronde is gesloten'
                                                    }>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (roundData.status === 'open') {
                                                                        openConfirmationDialog('start', roundId, roundData);
                                                                    } else if (roundData.status === 'running') {
                                                                        openConfirmationDialog('revert_to_open', roundId, roundData);
                                                                    }
                                                                }}
                                                                disabled={loading || !['open', 'running'].includes(roundData.status)}
                                                                color={roundData.status === 'running' ? 'success' : 'default'}
                                                            >
                                                                <SportsSoccerIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    {/* Slot icoon alleen zichtbaar wanneer status 'running' is */}
                                                    {roundData.status === 'running' && (
                                                        <Tooltip title="Ronde sluiten">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openConfirmationDialog('close', roundId, roundData);
                                                                }}
                                                                disabled={loading}
                                                                color="primary"
                                                            >
                                                                <LockOpenIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Slot icoon voor gesloten ronde */}
                                                    {roundData.status === 'closed' && (
                                                        <Tooltip title="Ronde heropenen">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openConfirmationDialog('reopen', roundId, roundData);
                                                                }}
                                                                disabled={loading}
                                                                color="primary"
                                                            >
                                                                <LockIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    Status: {roundData.status === 'open' ? 'Open - deelnemers kunnen captain en tactiek kiezen' :
                                                             roundData.status === 'running' ? 'Lopend - keuzes afgesloten' :
                                                             'Gesloten - punten berekend'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {(roundData.matches || []).map((match, matchIndex) => {
                                                        const matchId = `${roundId}-${matchIndex}`;
                                                        const homeUid = participantNameToUidMap.get(match.home);
                                                        const awayUid = participantNameToUidMap.get(match.away);

                                                        const homeSelection = matchSelections[homeUid]?.[matchId];
                                                        const awaySelection = matchSelections[awayUid]?.[matchId];

                                                        const homeCaptainChosen = !!homeSelection?.captain;
                                                        const awayCaptainChosen = !!awaySelection?.captain;
                                                        const homeTacticChosen = !!homeSelection?.tactic;
                                                        const awayTacticChosen = !!awaySelection?.tactic;

                                                        const homeCaptainName = homeSelection?.captain ? `${playerIdToNameMap.get(homeSelection.captain) || 'Onbekend'}` : 'Niet gekozen';
                                                        const awayCaptainName = awaySelection?.captain ? `${playerIdToNameMap.get(awaySelection.captain) || 'Onbekend'}` : 'Niet gekozen';
                                                        const homeTacticName = homeSelection?.tactic ? tacticsMap.get(homeSelection.tactic) || 'Onbekend' : 'Niet gekozen';
                                                        const awayTacticName = awaySelection?.tactic ? tacticsMap.get(awaySelection.tactic) || 'Onbekend' : 'Niet gekozen';

                                                        const KnockoutTeamDisplay = ({ teamName, isHome }) => {
                                                            const captainChosen = isHome ? homeCaptainChosen : awayCaptainChosen;
                                                            const tacticChosen = isHome ? homeTacticChosen : awayTacticChosen;
                                                            const captainName = isHome ? homeCaptainName : awayCaptainName;
                                                            const tacticName = isHome ? homeTacticName : awayTacticName;

                                                            return (
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    py: 0.5,
                                                                    px: 1,
                                                                    borderRadius: 1,
                                                                    border: '1px solid transparent'
                                                                }}>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography
                                                                            variant="body1"
                                                                            onClick={() => handleParticipantClick(teamName, roundData.roundId, roundData.status, matchId)}
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                                fontWeight: 'bold',
                                                                                fontSize: '0.875rem'
                                                                            }}
                                                                        >
                                                                            {teamName}
                                                                        </Typography>
                                                                        {showTacticsAndCaptains && (
                                                                            <>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        color: 'text.secondary',
                                                                                        fontSize: '0.75rem',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center'
                                                                                    }}
                                                                                >
                                                                                    <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                                    {captainName}
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        color: 'text.secondary',
                                                                                        fontSize: '0.75rem',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center'
                                                                                    }}
                                                                                >
                                                                                    <PsychologyIcon fontSize="inherit" sx={{ mr: 0.5, color: tacticChosen ? 'success.main' : 'error.main' }} />
                                                                                    {tacticName}
                                                                                </Typography>
                                                                            </>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        };

                                                        return (
                                                            <Box
                                                                key={matchId}
                                                                sx={{
                                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                                    borderRadius: '8px',
                                                                    overflow: 'hidden',
                                                                    transition: 'all 0.2s ease-in-out',
                                                                    '&:hover': {
                                                                        borderColor: 'rgba(255, 255, 255, 0.2)'
                                                                    }
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box sx={{ flex: 1, p: 1.5 }}>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                            <KnockoutTeamDisplay teamName={match.home} isHome={true} />
                                                                            <KnockoutTeamDisplay teamName={match.away} isHome={false} />
                                                                        </Box>
                                                                    </Box>
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        minWidth: '60px',
                                                                        py: 1.5,
                                                                        px: 1,
                                                                        borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                                                                    }}>
                                                                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                                            {matchScores[matchId]?.homeScore || '0'}
                                                                        </Typography>
                                                                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                                            {matchScores[matchId]?.awayScore || '0'}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}

                                {/* Totaal overzicht - zichtbaar als er min. 2 rondes zijn */}
                                {knockoutAchtsteRounds.length >= 2 && (
                                    <Accordion defaultExpanded={knockoutAchtsteRounds.every(([, rd]) => rd.status === 'closed')} sx={{ mb: 2, backgroundImage: 'none' }}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="knockout-totaal-content"
                                            id="knockout-totaal-header"
                                        >
                                            <Typography variant="h6">Totaal</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {(() => {
                                                    // Bereken totaalscores per wedstrijd over 2 rondes
                                                    const round1 = knockoutAchtsteRounds[0];
                                                    const round2 = knockoutAchtsteRounds[1];
                                                    const matches1 = round1?.[1]?.matches || [];

                                                    return matches1.map((match, matchIndex) => {
                                                        const matchId1 = `${round1[0]}-${matchIndex}`;
                                                        const matchId2 = `${round2[0]}-${matchIndex}`;

                                                        const homeScore1 = matchScores[matchId1]?.homeScore || 0;
                                                        const awayScore1 = matchScores[matchId1]?.awayScore || 0;
                                                        const homeScore2 = matchScores[matchId2]?.homeScore || 0;
                                                        const awayScore2 = matchScores[matchId2]?.awayScore || 0;

                                                        const totalHome = homeScore1 + homeScore2;
                                                        const totalAway = awayScore1 + awayScore2;

                                                        let homeWins = false;
                                                        let awayWins = false;
                                                        let tiebreaker = '';

                                                        if (totalHome > totalAway) {
                                                            homeWins = true;
                                                        } else if (totalAway > totalHome) {
                                                            awayWins = true;
                                                        } else {

                                                            // Simpele tiebreaker: bij gelijkstand wint wie in ronde 2 meer scoorde
                                                            // Bij echte gelijkstand: toon "Gelijk"
                                                            if (homeScore2 > awayScore2) {
                                                                homeWins = true;
                                                                tiebreaker = '(ronde 2)';
                                                            } else if (awayScore2 > homeScore2) {
                                                                awayWins = true;
                                                                tiebreaker = '(ronde 2)';
                                                            } else {
                                                                tiebreaker = '(gelijk)';
                                                            }
                                                        }

                                                        return (
                                                            <Box
                                                                key={`totaal-${matchIndex}`}
                                                                sx={{
                                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                                    borderRadius: '8px',
                                                                    overflow: 'hidden',
                                                                    transition: 'all 0.2s ease-in-out',
                                                                    '&:hover': {
                                                                        borderColor: 'rgba(255, 255, 255, 0.2)'
                                                                    }
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box sx={{ flex: 1, p: 1.5 }}>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1 }}>
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    sx={{
                                                                                        fontWeight: homeWins ? 'bold' : 'normal',
                                                                                        fontSize: '0.875rem',
                                                                                        color: homeWins ? 'success.main' : 'text.primary'
                                                                                    }}
                                                                                >
                                                                                    {match.home} {homeWins && tiebreaker ? tiebreaker : ''}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1 }}>
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    sx={{
                                                                                        fontWeight: awayWins ? 'bold' : 'normal',
                                                                                        fontSize: '0.875rem',
                                                                                        color: awayWins ? 'success.main' : 'text.primary'
                                                                                    }}
                                                                                >
                                                                                    {match.away} {awayWins && tiebreaker ? tiebreaker : ''}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        minWidth: '60px',
                                                                        py: 1.5,
                                                                        px: 1,
                                                                        borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                                                                    }}>
                                                                        <Typography sx={{
                                                                            fontSize: '1.1rem',
                                                                            fontWeight: 'bold',
                                                                            color: homeWins ? 'success.main' : 'text.primary'
                                                                        }}>
                                                                            {totalHome}
                                                                        </Typography>
                                                                        <Typography sx={{
                                                                            fontSize: '1.1rem',
                                                                            fontWeight: 'bold',
                                                                            color: awayWins ? 'success.main' : 'text.primary'
                                                                        }}>
                                                                            {totalAway}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    });
                                                })()}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </Box>
                        )}
                    </>
                )}

                {/* Tussenronde sectie - zichtbaar als spelfase Tussenronde is OF als er een tussenronde ranking bestaat */}
                {(auction?.spelfase === 'Tussenronde' || liveRankingIntermediateRound) && (
                    <>
                        <Typography variant="h5" gutterBottom>Tussenronde</Typography>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 2 },
                            mb: 3,
                            alignItems: { xs: 'stretch', sm: 'center' }
                        }}>
                            {!liveRankingIntermediateRound ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setConfirmStartIntermediateOpen(true)}
                                    disabled={loading || !finalRankingPoule}
                                    sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                                >
                                    Start tussenronde
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setConfirmResetIntermediateOpen(true)}
                                    disabled={loading}
                                    sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                                >
                                    Reset tussenronde
                                </Button>
                            )}
                        </Box>
                        {!finalRankingPoule && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                De poulefase moet eerst worden afgesloten voordat de tussenronde kan worden gestart.
                            </Alert>
                        )}

                        {/* Tussenronde rondes - alleen zichtbaar als tussenronde is gestart */}
                        {liveRankingIntermediateRound && intermediateRounds.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                {intermediateRounds.map(([roundId, roundData], index) => {
                                    // Bepaal ronde nummer: gebruik leg als beschikbaar, anders oude format
                                    let roundNumber;
                                    if (roundData.leg) {
                                        // Nieuwe format: leg is bijv. "1/2" -> neem eerste deel
                                        roundNumber = roundData.leg.split('/')[0];
                                    } else if (roundId.startsWith('intermediateRound')) {
                                        // Oude format
                                        roundNumber = roundId.replace('intermediateRound', '');
                                    } else {
                                        // Fallback: gebruik index + 1
                                        roundNumber = index + 1;
                                    }
                                    return (
                                        <Accordion key={roundId} defaultExpanded={index === 0} sx={{ mb: 2, backgroundImage: 'none' }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls={`intermediate-panel${index}-content`}
                                                id={`intermediate-panel${index}-header`}
                                                component="div"
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    '& .MuiAccordionSummary-content': {
                                                        margin: 0,
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        width: '100%'
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="h6">
                                                        Tussenronde {roundNumber}
                                                    </Typography>

                                                    {/* Voetbal icoon voor open/running toggle */}
                                                    <Tooltip title={
                                                        roundData.status === 'open' ? 'Ronde starten' :
                                                            roundData.status === 'running' ? 'Ronde terugzetten naar open' :
                                                                'Ronde is gesloten'
                                                    }>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (roundData.status === 'open') {
                                                                        openConfirmationDialog('start', roundId, roundData);
                                                                    } else if (roundData.status === 'running') {
                                                                        openConfirmationDialog('revert_to_open', roundId, roundData);
                                                                    }
                                                                }}
                                                                disabled={loading || !['open', 'running'].includes(roundData.status)}
                                                                color={roundData.status === 'running' ? 'success' : 'default'}
                                                            >
                                                                <SportsSoccerIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    {/* Slot icoon alleen zichtbaar wanneer status 'running' is */}
                                                    {roundData.status === 'running' && (
                                                        <Tooltip title="Tussenronde sluiten">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openConfirmationDialog('close', roundId, roundData);
                                                                }}
                                                                disabled={loading}
                                                                color="primary"
                                                            >
                                                                <LockOpenIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Slot icoon voor gesloten ronde */}
                                                    {roundData.status === 'closed' && (
                                                        <Tooltip title="Ronde heropenen">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openConfirmationDialog('reopen', roundId, roundData);
                                                                }}
                                                                disabled={loading}
                                                                color="primary"
                                                            >
                                                                <LockIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography variant="body2" color="text.secondary">
                                                    Status: {roundData.status === 'open' ? 'Open - deelnemers kunnen aanvoerder kiezen' : 
                                                             roundData.status === 'running' ? 'Lopend - aanvoerder keuze afgesloten' : 
                                                             'Gesloten - punten berekend'}
                                                </Typography>
                                                {roundData.roundId && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                        Sportmonks ronde ID: {roundData.roundId}
                                                    </Typography>
                                                )}

                                                {/* Overzicht aanvoerder-keuzes */}
                                                {liveRankingIntermediateRound && participants.length > 0 && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            Aanvoerder-keuzes ({participants.filter(p => {
                                                                const selectionId = `${roundId}-captain`;
                                                                return !!matchSelections[p.id]?.[selectionId]?.captain;
                                                            }).length}/{participants.length})
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            {participants.map((participant) => {
                                                                const selectionId = `${roundId}-captain`;
                                                                const participantSelection = matchSelections[participant.id]?.[selectionId] || {};
                                                                const captainChosen = !!participantSelection.captain;
                                                                const captainName = participantSelection.captain 
                                                                    ? (playerIdToNameMap.get(String(participantSelection.captain)) || 'Onbekend')
                                                                    : 'Niet gekozen';

                                                                return (
                                                                    <Box
                                                                        key={participant.id}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                            py: 0.5,
                                                                            px: 1,
                                                                            borderRadius: 1,
                                                                            bgcolor: 'action.hover'
                                                                        }}
                                                                    >
                                                                        <Typography variant="body2">
                                                                            {participant.name}
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <StarIcon 
                                                                                fontSize="small" 
                                                                                sx={{ color: captainChosen ? 'success.main' : 'error.main' }} 
                                                                            />
                                                                            <Typography 
                                                                                variant="body2" 
                                                                                sx={{ 
                                                                                    color: captainChosen ? 'text.primary' : 'text.secondary',
                                                                                    fontStyle: captainChosen ? 'normal' : 'italic'
                                                                                }}
                                                                            >
                                                                                {captainName}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Box>
                        )}
                    </>
                )}

                <Typography variant="h5" gutterBottom>Poulefase</Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 2 },
                    mb: 3,
                    alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                    <Button
                        variant="contained"
                        onClick={() => setConfirmLotingOpen(true)}
                        disabled={loading || sportmonksRounds.length === 0 || !['Voorbereiding', 'Veiling'].includes(auction?.spelfase)}
                        sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                    >
                        Nieuwe Loting
                    </Button>
                    {schedule && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setConfirmDeleteLotingOpen(true)}
                            disabled={loading || !['Voorbereiding', 'Veiling'].includes(auction?.spelfase)}
                            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                        >
                            Verwijder Loting
                        </Button>
                    )}
                    {schedule && !finalRankingPoule && (
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={() => setConfirmClosePouleOpen(true)}
                            disabled={loading}
                            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                        >
                            Afsluiten poulefase
                        </Button>
                    )}
                    {schedule && finalRankingPoule && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setConfirmReopenPouleOpen(true)}
                            disabled={loading}
                            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                        >
                            Heropenen poulefase
                        </Button>
                    )}
                    {schedule && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showTacticsAndCaptains}
                                    onChange={(e) => setShowTacticsAndCaptains(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Tactiek en aanvoerders tonen"
                            sx={{
                                ml: { xs: 0, sm: 2 },
                                mt: { xs: 1, sm: 0 }
                            }}
                        />
                    )}
                </Box>

                {schedule ? (
                    pouleRounds.map(([roundId, roundData], index) => (
                        <Accordion key={roundId} defaultExpanded={index === firstNonClosedRoundIndex} sx={{ mb: 2, backgroundImage: 'none' }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel${index}-content`}
                                id={`panel${index}-header`}
                                component="div"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '& .MuiAccordionSummary-content': {
                                        margin: 0,
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%'
                                    },
                                    '&.Mui-focusVisible': {
                                        backgroundColor: 'action.focus'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h6">
                                        Speelronde {roundId.replace('round', '')}
                                    </Typography>

                                    {/* Voetbal icoon voor open/running toggle */}
                                    <Tooltip title={
                                        roundData.status === 'open' ? 'Ronde starten' :
                                            roundData.status === 'running' ? 'Ronde terugzetten naar open' :
                                                'Ronde is gesloten'
                                    }>
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (roundData.status === 'open') {
                                                        openConfirmationDialog('start', roundId, roundData);
                                                    } else if (roundData.status === 'running') {
                                                        openConfirmationDialog('revert_to_open', roundId, roundData);
                                                    }
                                                }}
                                                disabled={loading || !['open', 'running'].includes(roundData.status)}
                                                color={roundData.status === 'running' ? 'success' : 'default'}
                                            >
                                                <SportsSoccerIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    {/* Slot icoon alleen zichtbaar wanneer status 'running' is */}
                                    {roundData.status === 'running' && (
                                        <Tooltip title="Speelronde sluiten">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openConfirmationDialog('close', roundId, roundData);
                                                }}
                                                disabled={loading}
                                                color="primary"
                                            >
                                                <LockOpenIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {/* Slot icoon voor gesloten ronde */}
                                    {roundData.status === 'closed' && (
                                        <Tooltip title="Ronde heropenen">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openConfirmationDialog('reopen', roundId, roundData);
                                                }}
                                                disabled={loading}
                                                color="primary"
                                            >
                                                <LockIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {roundData.matches.map((match, matchIndex) => {
                                        const matchId = `${roundId}-${matchIndex}`;
                                        const homeUid = participantNameToUidMap.get(match.home);
                                        const awayUid = participantNameToUidMap.get(match.away);

                                        const homeSelection = matchSelections[homeUid]?.[matchId];
                                        const awaySelection = matchSelections[awayUid]?.[matchId];

                                        const homeCaptainChosen = !!homeSelection?.captain;
                                        const awayCaptainChosen = !!awaySelection?.captain;
                                        const homeTacticChosen = !!homeSelection?.tactic;
                                        const awayTacticChosen = !!awaySelection?.tactic;

                                        const homeCaptainName = homeSelection?.captain ? `${playerIdToNameMap.get(homeSelection.captain) || 'Onbekend'}` : 'Niet gekozen';
                                        const awayCaptainName = awaySelection?.captain ? `${playerIdToNameMap.get(awaySelection.captain) || 'Onbekend'}` : 'Niet gekozen';

                                        const homeTacticName = homeSelection?.tactic ? tacticsMap.get(homeSelection.tactic) || 'Onbekend' : 'Niet gekozen';
                                        const awayTacticName = awaySelection?.tactic ? tacticsMap.get(awaySelection.tactic) || 'Onbekend' : 'Niet gekozen';

                                        const TeamDisplay = ({ teamName, roundId, isHome = false, matchId }) => {
                                            const captainChosen = isHome ? homeCaptainChosen : awayCaptainChosen;
                                            const tacticChosen = isHome ? homeTacticChosen : awayTacticChosen;
                                            const captainName = isHome ? homeCaptainName : awayCaptainName;
                                            const tacticName = isHome ? homeTacticName : awayTacticName;

                                            return (
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    py: 0.5,
                                                    px: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid transparent'
                                                }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="body1"
                                                            onClick={() => handleParticipantClick(teamName, roundData.roundId, roundData.status, matchId)}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.875rem'
                                                            }}
                                                        >
                                                            {teamName}
                                                        </Typography>
                                                        {showTacticsAndCaptains && (
                                                            <>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'text.secondary',
                                                                        fontSize: '0.75rem',
                                                                        display: 'flex',
                                                                        alignItems: 'center'
                                                                    }}
                                                                >
                                                                    <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                    {captainName}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'text.secondary',
                                                                        fontSize: '0.75rem',
                                                                        display: 'flex',
                                                                        alignItems: 'center'
                                                                    }}
                                                                >
                                                                    <PsychologyIcon fontSize="inherit" sx={{ mr: 0.5, color: tacticChosen ? 'success.main' : 'error.main' }} />
                                                                    {tacticName}
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        };

                                        return (
                                            <Box
                                                key={matchId}
                                                sx={{
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        borderColor: 'rgba(255, 255, 255, 0.2)'
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box sx={{ flex: 1, p: 1.5 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <TeamDisplay teamName={match.home} roundId={roundData.roundId} isHome={true} matchId={matchId} />
                                                            <TeamDisplay teamName={match.away} roundId={roundData.roundId} isHome={false} matchId={matchId} />
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: '60px',
                                                        py: 1.5,
                                                        px: 1,
                                                        borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                                                    }}>
                                                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                            {matchScores[matchId]?.homeScore || '0'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                            {matchScores[matchId]?.awayScore || '0'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))
                ) : (
                    <Typography>Er is nog geen loting uitgevoerd. Klik op "Nieuwe Loting" om te beginnen.</Typography>
                )}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>Blinde Pool</Typography>
                    <Button variant="contained" onClick={handleStartBlindPoolRound} disabled={loading || blindPoolRounds.find(r => r.status === 'open')}> 
                        Start Nieuwe Ronde
                    </Button>
                </Box>

                {loading && <CircularProgress />}

                {error && <Alert severity="error">{error}</Alert>}

                {!loading && !error && blindPoolRounds.length === 0 && (
                    <Typography>Er zijn nog geen blinde pool rondes gestart.</Typography>
                )}

                {!loading && !error && blindPoolRounds.map(round => (
                    <BlindPoolRound key={round.id} round={round} onAssignPlayers={handleAssignPlayers} onRecalculate={handleRecalculate} onDeleteRound={handleOpenConfirmDeleteRound} onDeleteBid={handleOpenConfirmDeleteBid} transfers={transfers} />
                ))}
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>Draft</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {(!draftConfig || draftConfig.status === 'not_started') && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setConfirmDraftLotingOpen(true)}
                                disabled={loading || !draftConfig || auction?.spelfase !== 'Draft'}
                            >
                                Loting
                            </Button>
                        )}
                        {draftConfig?.status === 'lottery_done' && (
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => setConfirmDraftStartOpen(true)}
                                disabled={loading}
                            >
                                Draft starten
                            </Button>
                        )}
                        {draftConfig && ['lottery_done', 'active'].includes(draftConfig.status) && (
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={() => setConfirmDraftResetLotingOpen(true)}
                                disabled={loading}
                            >
                                Loting opnieuw
                            </Button>
                        )}
                        {draftConfig && ['active', 'lottery_done'].includes(draftConfig.status) && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setConfirmDraftCloseOpen(true)}
                                disabled={loading}
                            >
                                Draft sluiten
                            </Button>
                        )}
                        {draftConfig?.status === 'completed' && (
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={() => setConfirmDraftRestartOpen(true)}
                                disabled={loading}
                            >
                                Draft herstarten
                            </Button>
                        )}
                    </Box>
                </Box>

                {!draftConfig && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Zet de spelfase op "Draft" om de draft te configureren.
                    </Alert>
                )}

                {draftConfig && (
                    <>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Status: {draftConfig.status === 'not_started' ? 'Nog niet geloot' :
                                     draftConfig.status === 'lottery_done' ? 'Geloot - klaar om te starten' :
                                     draftConfig.status === 'active' ? 'Actief' :
                                     draftConfig.status === 'completed' ? 'Afgerond' : draftConfig.status}
                        </Alert>

                        {/* Draft deelnemers */}
                        <Typography variant="h6" gutterBottom>Deelnemers ({draftParticipantsWithNames.length})</Typography>
                        <TableContainer component={Paper} sx={{ mb: 4 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Naam</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {draftParticipantsWithNames.map((p, index) => (
                                        <TableRow key={p.uid}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{p.name}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Draft volgorde (snake) */}
                        {draftConfig.draftOrder && draftConfig.draftOrder.length > 0 && (
                            <>
                                <Typography variant="h6" gutterBottom>Draft volgorde (Snake)</Typography>
                                <TableContainer component={Paper} sx={{ mb: 4 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Pick</TableCell>
                                                <TableCell>Deelnemer</TableCell>
                                                <TableCell>Gekozen speler</TableCell>
                                                {draftConfig.status === 'active' && <TableCell align="right">Beurt</TableCell>}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {draftConfig.draftOrder.map((uid, index) => {
                                                const participant = participants.find(p => (p.uid || p.id) === uid);
                                                const isCurrentPick = draftConfig.status === 'active' && index === draftConfig.currentPickIndex;
                                                const draftTransfer = draftTransfers.find(t => t.draftPickIndex === index && t.to !== 'vrije-markt');

                                                return (
                                                    <TableRow 
                                                        key={index}
                                                        sx={{
                                                            fontWeight: isCurrentPick ? 'bold' : 'normal',
                                                            bgcolor: isCurrentPick ? 'action.selected' : 
                                                                     index < (draftConfig.draftOrder.length / 2) ? 'transparent' : 'action.hover'
                                                        }}
                                                    >
                                                        <TableCell sx={{ fontWeight: isCurrentPick ? 'bold' : 'normal' }}>
                                                            {index + 1}
                                                            {index === (draftConfig.draftOrder.length / 2) - 1 && ' ↩'}
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: isCurrentPick ? 'bold' : 'normal' }}>
                                                            {participant?.name || 'Onbekend'}
                                                            {isCurrentPick && ' 👈'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {draftTransfer ? (
                                                                <>
                                                                    {draftTransfer.playerName}
                                                                    {draftTransfer.profitPercentage > 0 && (
                                                                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'success.main', fontWeight: 'bold' }}>
                                                                            {draftTransfer.profitPercentage}%
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            ) : (index < draftConfig.currentPickIndex ? '—' : '')}
                                                        </TableCell>
                                                        {draftConfig.status === 'active' && (
                                                            <TableCell align="right">
                                                                {!isCurrentPick && (
                                                                    <Tooltip title={`Zet beurt op pick ${index + 1}`}>
                                                                        <span>
                                                                            <Button
                                                                                variant="outlined"
                                                                                size="small"
                                                                                onClick={() => handleSetDraftTurn(index)}
                                                                                disabled={loading}
                                                                            >
                                                                                Zet beurt
                                                                            </Button>
                                                                        </span>
                                                                    </Tooltip>
                                                                )}
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}

                        {/* Uitgeschakelde deelnemers */}
                        <Typography variant="h6" gutterBottom>Uitgeschakelde deelnemers ({draftEliminatedWithNames.length})</Typography>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Naam</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {draftEliminatedWithNames.map((p, index) => (
                                        <TableRow key={p.uid}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{p.name}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </TabPanel>

            {/* Transferrondes tab */}
            <TabPanel value={tabValue} index={4}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>Transferrondes</Typography>
                </Box>

                {!transferRoundAllowed && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Transferrondes zijn niet beschikbaar in de huidige spelfase ({auction?.spelfase || 'Onbekend'}).
                        Dit is alleen mogelijk in de Knock-out fase.
                    </Alert>
                )}

                {transferRoundAllowed && (
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>Nieuwe transferronde starten</Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth disabled={trLoadingStages} size="small">
                                    <InputLabel>Toernooifase</InputLabel>
                                    <Select
                                        value={trSelectedStage}
                                        label="Toernooifase"
                                        onChange={(e) => {
                                            setTrSelectedStage(e.target.value);
                                            setTrSelectedLeg('');
                                            setTrSelectedRound('');
                                        }}
                                    >
                                        {trStages.map(stage => (
                                            <MenuItem key={stage.id} value={stage.id}>
                                                {getTranslatedStageName(stage.name)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {trIsLeague && (
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth disabled={!trSelectedStage || trLoadingRounds} size="small">
                                        <InputLabel>Speelronde</InputLabel>
                                        <Select value={trSelectedRound} label="Speelronde" onChange={(e) => setTrSelectedRound(e.target.value)}>
                                            {trRounds.map(round => (
                                                <MenuItem key={round.id} value={round.id}>
                                                    {round.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            {trSelectedStage && !trIsLeague && (
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Wedstrijd</InputLabel>
                                        <Select value={trSelectedLeg} label="Wedstrijd" onChange={(e) => setTrSelectedLeg(e.target.value)}>
                                            <MenuItem value="1">1/2</MenuItem>
                                            <MenuItem value="2">2/2</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="contained"
                                    onClick={() => setConfirmStartTransferRoundOpen(true)}
                                    disabled={
                                        loading ||
                                        !trCanProceed ||
                                        !!transferRounds.find(r => r.status === 'open') ||
                                        !transferRoundPreview
                                    }
                                >
                                    Start Nieuwe Transferronde
                                </Button>
                            </Grid>
                        </Grid>

                        {/* Preview van selectie */}
                        {trCanProceed && transferRoundPreview && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Preview: de volgende deelnemers worden geselecteerd
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {Object.entries(transferRoundPreview).map(([id, data]) => (
                                        <Chip
                                            key={id}
                                            label={`${data.name} (${data.allowance})`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {trCanProceed && !transferRoundPreview && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Geen deelnemers beschikbaar voor een transferronde met deze selectie.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                )}

                {loading && <CircularProgress />}

                {error && <Alert severity="error">{error}</Alert>}

                {!loading && !error && transferRounds.length === 0 && (
                    <Typography>Er zijn nog geen transferrondes gestart.</Typography>
                )}

                {!loading && !error && transferRounds.map(round => (
                    <TransferRound
                        key={round.id}
                        round={round}
                        liveParticipants={participants}
                        transfers={transfers}
                        computedTransfersLeftMap={computedTransfersLeftMap}
                        onCloseRound={(roundId) => {
                            setTransferRoundToAction(roundId);
                            setConfirmCloseTransferRoundOpen(true);
                        }}
                        onDeleteRound={(roundId) => {
                            setTransferRoundToAction(roundId);
                            setConfirmDeleteTransferRoundOpen(true);
                        }}
                        onDeleteTransfer={handleDeleteTransfer}
                    />
                ))}
            </TabPanel>

            {/* Draft bevestigings-dialogen */}
            <Dialog open={confirmDraftLotingOpen} onClose={() => setConfirmDraftLotingOpen(false)}>
                <DialogTitle>Draft Loting Uitvoeren?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de draft loting wilt uitvoeren? De volgorde wordt willekeurig bepaald.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDraftLotingOpen(false)}>Annuleren</Button>
                    <Button onClick={handleDraftLoting} color="primary">Bevestigen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDraftStartOpen} onClose={() => setConfirmDraftStartOpen(false)}>
                <DialogTitle>Draft Starten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de draft wilt starten? Deelnemers kunnen dan hun keuzes maken.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDraftStartOpen(false)}>Annuleren</Button>
                    <Button onClick={handleDraftStart} color="success">Starten</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDraftResetLotingOpen} onClose={() => setConfirmDraftResetLotingOpen(false)}>
                <DialogTitle>Draft Loting Opnieuw?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de draft loting opnieuw wilt doen? Alle eventuele draft-keuzes worden verwijderd.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDraftResetLotingOpen(false)}>Annuleren</Button>
                    <Button onClick={handleDraftResetLoting} color="warning">Opnieuw loten</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDraftCloseOpen} onClose={() => setConfirmDraftCloseOpen(false)}>
                <DialogTitle>Draft Sluiten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de draft wilt sluiten? Dit kan niet meer ongedaan worden gemaakt.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDraftCloseOpen(false)}>Annuleren</Button>
                    <Button onClick={handleDraftClose} color="error">Sluiten</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDraftRestartOpen} onClose={() => setConfirmDraftRestartOpen(false)}>
                <DialogTitle>Draft Herstarten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de draft opnieuw wilt starten? Alle draft-keuzes worden teruggedraaid, 
                        spelers gaan terug naar hun oorspronkelijke eigenaar, en alle aanbiedingen worden verwijderd.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDraftRestartOpen(false)}>Annuleren</Button>
                    <Button onClick={handleDraftRestart} color="warning">Herstarten</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openAssignmentModal}
                onClose={handleCloseAssignmentModal}
                aria-labelledby="assignment-dialog-title"
            >
                <DialogTitle id="assignment-dialog-title">Voorgestelde Spelerstoekenning</DialogTitle>
                <DialogContent>
                    {proposedAssignments.length === 0 ? (
                        <DialogContentText>
                            Geen spelers toe te kennen in deze ronde.
                        </DialogContentText>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Speler</TableCell>
                                        <TableCell>Deelnemer</TableCell>
                                        <TableCell align="right">Bedrag</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {proposedAssignments.map((assignment, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{assignment.playerName}</TableCell>
                                            <TableCell>{assignment.participantName}</TableCell>
                                            <TableCell align="right">€{assignment.amount}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAssignmentModal}>Annuleren</Button>
                    <Button onClick={handleConfirmAssignments} autoFocus disabled={proposedAssignments.length === 0}>
                        Bevestigen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openRecalculateModal}
                onClose={handleCloseRecalculateModal}
                aria-labelledby="recalculate-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="recalculate-dialog-title">Toekenningen herbepalen</DialogTitle>
                <DialogContent>
                    {recalculatePlan && recalculatePlan.toDelete.length === 0 && recalculatePlan.toCreate.length === 0 ? (
                        <DialogContentText>
                            De toekenningen kloppen al. Er zijn geen wijzigingen nodig.
                        </DialogContentText>
                    ) : recalculatePlan && (
                        <>
                            <DialogContentText sx={{ mb: 2 }}>
                                De onderstaande wijzigingen worden doorgevoerd. Budget en resterende
                                transfers worden automatisch gecorrigeerd.
                            </DialogContentText>

                            {recalculatePlan.toDelete.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" color="error" sx={{ mt: 1 }}>
                                        Te verwijderen toekenningen ({recalculatePlan.toDelete.length})
                                    </Typography>
                                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Speler</TableCell>
                                                    <TableCell>Deelnemer</TableCell>
                                                    <TableCell align="right">Bedrag</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {recalculatePlan.toDelete.map((t, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{t.playerName}</TableCell>
                                                        <TableCell>{participants.find(p => (p.uid || p.id) === t.to)?.name || t.to}</TableCell>
                                                        <TableCell align="right">€{t.amount}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {recalculatePlan.toCreate.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" color="success.main" sx={{ mt: 1 }}>
                                        Nieuwe toekenningen ({recalculatePlan.toCreate.length})
                                    </Typography>
                                    <TableContainer component={Paper}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Speler</TableCell>
                                                    <TableCell>Deelnemer</TableCell>
                                                    <TableCell align="right">Bedrag</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {recalculatePlan.toCreate.map((a, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{a.playerName}</TableCell>
                                                        <TableCell>{a.participantName}</TableCell>
                                                        <TableCell align="right">€{a.amount}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRecalculateModal}>Annuleren</Button>
                    <Button
                        onClick={handleConfirmRecalculate}
                        autoFocus
                        disabled={!recalculatePlan || (recalculatePlan.toDelete.length === 0 && recalculatePlan.toCreate.length === 0)}
                    >
                        Doorvoeren
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDeleteRoundOpen}
                onClose={handleCloseConfirmDeleteRound}
            >
                <DialogTitle>Ronde Verwijderen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je deze blinde pool ronde wilt verwijderen? Alle biedingen en toegekende spelers in deze ronde gaan verloren. Deze actie kan niet ongedaan worden gemaakt.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDeleteRound}>Annuleren</Button>
                    <Button onClick={handleConfirmDeleteRound} color="error">Verwijderen</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmLotingOpen}
                onClose={() => setConfirmLotingOpen(false)}
            >
                <DialogTitle>Nieuwe Loting Uitvoeren?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je een nieuwe loting wilt uitvoeren? De huidige loting en het bijbehorende speelschema worden volledig verwijderd en vervangen.
                        {!['Voorbereiding', 'Veiling'].includes(auction?.spelfase) && (
                            <><br /><br /><strong>Let op:</strong> In de huidige spelfase ({auction?.spelfase}) mag geen nieuwe loting worden uitgevoerd. Dit kan alleen in de spelfases Voorbereiding en Veiling.</>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmLotingOpen(false)}>Annuleren</Button>
                    <Button
                        onClick={handleLoting}
                        color="primary"
                        disabled={!['Voorbereiding', 'Veiling'].includes(auction?.spelfase)}
                    >
                        Bevestigen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDeleteLotingOpen}
                onClose={() => setConfirmDeleteLotingOpen(false)}
            >
                <DialogTitle>Loting Verwijderen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de volledige loting wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
                        {!['Voorbereiding', 'Veiling'].includes(auction?.spelfase) && (
                            <><br /><br /><strong>Let op:</strong> In de huidige spelfase ({auction?.spelfase}) mag de loting niet verwijderd worden. Dit kan alleen in de spelfases Voorbereiding en Veiling.</>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteLotingOpen(false)}>Annuleren</Button>
                    <Button
                        onClick={handleDeleteLoting}
                        color="error"
                        disabled={!['Voorbereiding', 'Veiling'].includes(auction?.spelfase)}
                    >
                        Verwijderen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDeleteBidOpen}
                onClose={handleCloseConfirmDeleteBid}
            >
                <DialogTitle>Bieding Verwijderen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je deze bieding wilt verwijderen? De geresulteerde transfer wordt hiermee ook verwijderd.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDeleteBid}>Annuleren</Button>
                    <Button onClick={handleConfirmDeleteBid} color="error">Verwijderen</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmClosePouleOpen}
                onClose={() => setConfirmClosePouleOpen(false)}
            >
                <DialogTitle>Poulefase Afsluiten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de poulefase wilt afsluiten? De huidige ranglijst wordt opgeslagen als eindstand van de poulefase en de live ranglijst wordt gereset naar 0 punten voor alle deelnemers.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmClosePouleOpen(false)}>Annuleren</Button>
                    <Button onClick={handleClosePoulePhase} color="warning">Afsluiten</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmReopenPouleOpen}
                onClose={() => setConfirmReopenPouleOpen(false)}
            >
                <DialogTitle>Poulefase Heropenen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de poulefase wilt heropenen? De eindstand van de poulefase wordt hersteld naar de live ranglijst en de opgeslagen eindstand wordt verwijderd.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmReopenPouleOpen(false)}>Annuleren</Button>
                    <Button onClick={handleReopenPoulePhase} color="secondary">Heropenen</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmStartIntermediateOpen}
                onClose={() => setConfirmStartIntermediateOpen(false)}
            >
                <DialogTitle>Tussenronde Starten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de tussenronde wilt starten? Alle deelnemers behalve de top 15 van de poulefase worden toegevoegd aan de tussenronde met 0 punten.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmStartIntermediateOpen(false)}>Annuleren</Button>
                    <Button onClick={handleStartIntermediateRound} color="primary">Starten</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmResetIntermediateOpen}
                onClose={() => setConfirmResetIntermediateOpen(false)}
            >
                <DialogTitle>Tussenronde Resetten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de tussenronde wilt resetten? De tussenronde ranglijst wordt verwijderd.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmResetIntermediateOpen(false)}>Annuleren</Button>
                    <Button onClick={handleResetIntermediateRound} color="error">Resetten</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmStartFinalRoundOpen}
                onClose={() => setConfirmStartFinalRoundOpen(false)}
            >
                <DialogTitle>Finaleronde Starten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de finaleronde wilt starten? De top 8 deelnemers van de tussenronde worden toegevoegd aan de finaleronde met 0 punten.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmStartFinalRoundOpen(false)}>Annuleren</Button>
                    <Button onClick={handleStartFinalRound} color="primary">Starten</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmResetFinalRoundOpen}
                onClose={() => setConfirmResetFinalRoundOpen(false)}
            >
                <DialogTitle>Finaleronde Resetten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de finaleronde wilt resetten? De finaleronde ranglijst en alle data worden verwijderd.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmResetFinalRoundOpen(false)}>Annuleren</Button>
                    <Button onClick={handleResetFinalRound} color="error">Resetten</Button>
                </DialogActions>
            </Dialog>

            {/* Knock-out bevestigings-dialogen */}
            <Dialog
                open={confirmKnockoutLotingOpen}
                onClose={() => setConfirmKnockoutLotingOpen(false)}
            >
                <DialogTitle>Knock-out Loting Uitvoeren?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {knockoutAchtsteRounds.length > 0
                            ? 'Er bestaat al een loting. Weet je zeker dat je de loting opnieuw wilt uitvoeren? De bestaande wedstrijden worden vervangen.'
                            : 'Weet je zeker dat je de knock-out loting wilt uitvoeren? De top 15 van de poulefase en de #1 van de tussenronde worden ingedeeld in 8 wedstrijden over 2 speelrondes.'
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmKnockoutLotingOpen(false)}>Annuleren</Button>
                    <Button onClick={handleKnockoutLoting} color="primary">Loting uitvoeren</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmResetKnockoutLotingOpen}
                onClose={() => setConfirmResetKnockoutLotingOpen(false)}
            >
                <DialogTitle>Knock-out Loting Verwijderen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je de knock-out loting wilt verwijderen? Alle achtste finale wedstrijden en bijbehorende resultaten worden verwijderd.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmResetKnockoutLotingOpen(false)}>Annuleren</Button>
                    <Button onClick={handleResetKnockoutLoting} color="error">Verwijderen</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmAction.open}
                onClose={() => setConfirmAction({ ...confirmAction, open: false })}
            >
                <DialogTitle>{confirmAction.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmAction.content}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAction({ ...confirmAction, open: false })}>Annuleren</Button>
                    <Button onClick={handleActionConfirmation} color={confirmAction.type === 'reopen' ? 'error' : 'primary'} autoFocus>
                        Bevestigen
                    </Button>
                </DialogActions>
            </Dialog>

            {selectedParticipantForModal && (
                <ParticipantTeamModal
                    open={isParticipantModalOpen}
                    onClose={handleCloseParticipantModal}
                    auctionId={auctionId}
                    participant={selectedParticipantForModal}
                    roundId={modalRoundId}
                    seasonId={auction?.season}
                    roundStatus={modalRoundStatus}
                    matchId={modalMatchId}
                />
            )}

            {/* Transferrondes bevestigings-dialogen */}
            <Dialog open={confirmStartTransferRoundOpen} onClose={() => setConfirmStartTransferRoundOpen(false)}>
                <DialogTitle>Transferronde Starten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je een nieuwe transferronde wilt starten?
                        De transfermarkt wordt gesloten voor alle deelnemers die niet geselecteerd zijn.
                    </DialogContentText>
                    {transferRoundPreview && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Geselecteerde deelnemers:</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Deelnemer</TableCell>
                                        <TableCell align="right">Transfermogelijkheden</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(transferRoundPreview).map(([id, data]) => (
                                        <TableRow key={id}>
                                            <TableCell>{data.name}</TableCell>
                                            <TableCell align="right">{data.allowance}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmStartTransferRoundOpen(false)}>Annuleren</Button>
                    <Button onClick={handleStartTransferRound} color="primary" disabled={loading}>Starten</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmCloseTransferRoundOpen} onClose={() => setConfirmCloseTransferRoundOpen(false)}>
                <DialogTitle>Transferronde Sluiten?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je deze transferronde wilt sluiten? Deelnemers kunnen daarna geen transfers meer doen in deze ronde.
                        De transfermarkt blijft gesloten totdat je deze handmatig heropent via het Admin Overzicht.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setConfirmCloseTransferRoundOpen(false); setTransferRoundToAction(null); }}>Annuleren</Button>
                    <Button onClick={handleCloseTransferRound} color="warning" disabled={loading}>Sluiten</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteTransferRoundOpen} onClose={() => setConfirmDeleteTransferRoundOpen(false)}>
                <DialogTitle>Transferronde Verwijderen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Weet je zeker dat je deze transferronde wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
                        Al gedane transfers in deze ronde worden hiermee niet teruggedraaid.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setConfirmDeleteTransferRoundOpen(false); setTransferRoundToAction(null); }}>Annuleren</Button>
                    <Button onClick={handleDeleteTransferRound} color="error" disabled={loading}>Verwijderen</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminGame;